import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';

import type {
  Session,
  PostgrestSingleResponse,
  PostgrestResponse,
  PostgrestError,
} from '@supabase/supabase-js';

import {
  Provider,
  Treatment,
  BlogPost,
  Doctor,
  MedicalProcedure,
  Equipment,
  ImageSEOMetadata,
  PageSEO,
  Inquiry,
  Translation,
  ProviderEquipment,
} from '@/api/entities';
import type { EquipmentType } from '@/api/entities/Equipment';
import { supabase } from '@/integrations/supabase/client';
import type {
  Database,
  Tables,
  TablesInsert,
  TablesUpdate,
  Json,
} from '@/integrations/supabase/types';
import type { Branch } from '@/types/database';
import type { User } from '@/types/user';
import { formatDate } from '@/utils';
import {
  loadProvidersForTreatment,
  loadSingleProvider,
  loadProviderRelations,
  loadPreviewProvider,
  type PreviewProviderData,
} from '@/utils/provider/providerDataLoader';
import { fetchTreatmentsByNames } from '@/utils/supabase/queryHelpers';

// Type for ProviderEquipment with custom methods
interface ProviderEquipmentWithDetails {
  [key: string]: unknown;
  equipment: {
    [key: string]: unknown;
    galleryImages: string[];
    mainPhotoUrl: string | null;
  };
}

type ProviderEquipmentWithCustomMethods = typeof ProviderEquipment & {
  getByProvider: (providerId: string) => Promise<ProviderEquipmentWithDetails[]>;
};

// Type for PageSEO with custom methods
type PageSEOWithCustomMethods = typeof PageSEO & {
  getByPageKey: (pageKey: string, languageCode?: string) => Promise<Tables<'page_seo'> | null>;
};

// Type aliases for complex return types
type ProvidersForTreatmentResult = {
  treatment: Tables<'treatments'>;
  providers: Tables<'providers'>[];
  doctors: Tables<'doctors'>[];
};

type ProviderRelationsResult = {
  treatments: Tables<'treatments'>[];
  doctors: Tables<'doctors'>[];
};

// Query Keys
export const queryKeys = {
  providers: {
    all: ['providers', 'all'],
    byId: (id: string) => ['providers', id],
    orderingPreference: (clinicId: string) => ['providers', 'orderingPreference', clinicId],
    byClinicIds: (clinicIds: string[]) => ['providers', 'byClinicIds', clinicIds],
    featured: (limit: number) => ['providers', 'featured', limit],
  },
  treatments: {
    all: ['treatments', 'all'],
    byId: (id: string) => ['treatments', id],
    byClinic: (clinicId: string) => ['treatments', 'byClinic', clinicId],
    linked: (codes: string[]) => ['treatments', 'linked', codes],
    byProviderIds: (providerIds: string[]) => ['treatments', 'byProviderIds', providerIds],
    byNames: (treatmentNames: string[]) => ['treatments', 'byNames', treatmentNames],
    home: (limit: number) => ['treatments', 'home', limit],
    mostViewed: (limit: number, daysAgo: number) => ['treatments', 'mostViewed', limit, daysAgo],
  },
  blogPosts: {
    all: ['blogPosts', 'all'],
    byId: (id: string) => ['blogPosts', id],
  },
  doctors: {
    all: ['doctors', 'all'],
    byId: (id: string) => ['doctors', id],
    byIdOrPrefix: (id: string) => ['doctors', 'byIdOrPrefix', id],
    byClinic: (clinicId: string) => ['doctors', 'byClinic', clinicId],
    byProviderIds: (providerIds: string[]) => ['doctors', 'byProviderIds', providerIds],
  },
  procedures: {
    all: ['procedures', 'all'],
    byId: (id: string) => ['procedures', id],
    byIds: (procedureIds: string[]) => ['procedures', 'byIds', procedureIds],
    home: (limit: number) => ['procedures', 'home', limit],
  },
  equipment: {
    all: ['equipment', 'all'],
    byId: (id: string) => ['equipment', id],
  },
  imageSeo: {
    all: ['imageSeo', 'all'],
    map: ['imageSeo', 'map'],
    byId: (id: string) => ['imageSeo', id],
    byImageUrl: (url: string) => ['imageSeo', 'byImageUrl', url],
    byEntity: (type: string, id: string) => ['imageSeo', 'byEntity', type, id],
    altText: (imageUrl: string) => ['imageSeo', 'altText', imageUrl],
  },
  pageSeo: {
    byPageKey: (pageKey: string, languageCode: string = 'en') => ['pageSeo', pageKey, languageCode],
  },
  inquiries: {
    all: ['inquiries', 'all'],
    byId: (id: string) => ['inquiries', id],
    filtered: (filters: Record<string, unknown>) => ['inquiries', 'filtered', filters],
    count: {
      new: (userId: string, assignedProviderIds: string[]) => [
        'inquiries',
        'count',
        'new',
        userId,
        assignedProviderIds,
      ],
    },
  },
  translations: {
    all: ['translations', 'all'],
    byId: (id: string) => ['translations', id],
    byLocale: (locale: string) => ['translations', 'locale', locale],
  },
  search: (params: Record<string, unknown>) => ['search', params],
  providerData: {
    byTreatment: (treatmentId: string) => ['providerData', 'byTreatment', treatmentId],
    single: (providerId: string) => ['providerData', 'single', providerId],
    preview: (previewId: string) => ['providerData', 'preview', previewId],
    relations: (
      providerId: string,
      treatmentKey: string = '',
      _options: Record<string, unknown> = {}
    ) => ['providerData', 'relations', providerId, treatmentKey],
  },
  user: {
    profile: ['user', 'profile'],
  },
  admin: {
    stats: ['admin', 'stats'],
    providersWithTodos: ['admin', 'providers', 'withTodos'],
    providers: (batchesToLoad: number[]) => ['admin', 'providers', batchesToLoad],
    doctors: (batchesToLoad: number[]) => ['admin', 'doctors', batchesToLoad],
    clinics: ['admin', 'clinics', 'list'],
    procedures: (batchesToLoad: number[]) => ['admin', 'procedures', batchesToLoad],
    treatments: (batchesToLoad: number[]) => ['admin', 'treatments', batchesToLoad],
    treatmentStats: ['admin', 'treatments', 'stats'],
    inquiries: {
      newCount: (userId: string) => ['admin', 'inquiries', 'newCount', userId],
    },
    reports: {
      pendingCount: (userId: string) => ['admin', 'reports', 'pendingCount', userId],
    },
  },
  favorites: {
    all: (userId: string) => ['favorites', 'all', userId],
    byEntity: (userId: string, entityType: string, entityId: string) => [
      'favorites',
      userId,
      entityType,
      entityId,
    ],
  },
  stats: {
    providers: ['stats', 'providers', 'count'],
    doctors: ['stats', 'doctors', 'count'],
    treatments: ['stats', 'treatments', 'count'],
    equipments: ['stats', 'equipments', 'count'],
  },
  pageViews: {
    recentViewers: (providerId: string) => ['pageViews', 'recentViewers', providerId],
  },
  partners: {
    all: ['partners', 'all'],
    doctors: (assignedProviderIds: string[]) => ['partners', 'doctors', assignedProviderIds],
    clinics: (assignedProviderIds: string[]) => ['partners', 'clinics', assignedProviderIds],
  },
  transportation: {
    requests: (partnerId: string) => ['transportation', 'requests', partnerId],
    partners: ['transportation', 'partners'],
    bankAccounts: (userId: string) => ['transportation', 'bankAccounts', userId],
  },
};

// Providers
export function useProviders(
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'providers'>[], Error> {
  return useQuery({
    queryKey: queryKeys.providers.all,
    queryFn: fetchProviders,
    staleTime: 1000 * 60 * 15,
    ...options, // Allow overriding default options (e.g., enabled)
  });
}

export function useProvider(id: string): UseQueryResult<Tables<'providers'>, Error> {
  return useQuery({
    queryKey: queryKeys.providers.byId(id),
    queryFn: () => fetchProvider(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

// Optimized query for related providers - only fetches similar providers
export function useRelatedProviders(
  provider: Database['public']['Tables']['providers']['Row'] | undefined,
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'providers'>[], Error> {
  return useQuery({
    queryKey: ['providers', 'related', provider?.id],
    queryFn: async (): Promise<Tables<'providers'>[]> => {
      if (!provider) return [];

      const SELECT_FIELDS =
        'id, name, name_translations, city, district, location, specialties, facility_type, image_urls, description, description_translations, verified';

      // Try strict query first
      const primaryData = await fetchRelatedProviders(provider, SELECT_FIELDS, true);

      // If insufficient results, fall back to broader search
      if (primaryData.length >= 5) {
        return primaryData;
      }

      return fetchRelatedProviders(provider, SELECT_FIELDS, false);
    },
    enabled: !!provider && options.enabled !== false,
    staleTime: 1000 * 60 * 15, // 15 minutes cache
    ...options,
  });
}

// Treatments
export function useTreatments(
  orderBy: string | undefined = undefined,
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'treatments'>[], Error> {
  const queryKey = orderBy ? [...queryKeys.treatments.all, orderBy] : queryKeys.treatments.all;
  return useQuery<Tables<'treatments'>[], Error>({
    queryKey,
    queryFn: async (): Promise<Tables<'treatments'>[]> => await fetchTreatments(orderBy),
    staleTime: 1000 * 60 * 60,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
}

// Equipment
export function useEquipmentList(
  options: Record<string, unknown> = {}
): UseQueryResult<EquipmentType[], Error> {
  return useQuery({
    queryKey: queryKeys.equipment.all,
    queryFn: fetchEquipmentList,
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useTreatment(id: string): UseQueryResult<Tables<'treatments'>, Error> {
  return useQuery({
    queryKey: queryKeys.treatments.byId(id),
    queryFn: () => fetchTreatment(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

// Doctors
export function useDoctors(): UseQueryResult<Tables<'doctors'>[], Error> {
  return useQuery({
    queryKey: queryKeys.doctors.all,
    queryFn: fetchDoctors,
    staleTime: 1000 * 60 * 15,
  });
}

export function useDoctor(id: string): UseQueryResult<Tables<'doctors'>, Error> {
  return useQuery({
    queryKey: queryKeys.doctors.byId(id),
    queryFn: () => fetchDoctor(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

// Blog posts staged loading
export function useBlogCorePosts(
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'blog_posts'>[], Error> {
  return useQuery({
    queryKey: ['blogPosts', 'core'],
    queryFn: () =>
      fetchBlogPosts({
        fields: [
          'id',
          'title',
          'excerpt',
          'category',
          'created_at',
          'updated_at',
          'published',
          'image_url',
          'image_alt_text',
        ],
      }),
    staleTime: 1000 * 60 * 15,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

export function useBlogSupplementalPosts(
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'blog_posts'>[], Error> {
  return useQuery({
    queryKey: ['blogPosts', 'supplemental'],
    queryFn: () => fetchBlogPosts({}),
    staleTime: 1000 * 60 * 15,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

export function useBlogPosts(
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'blog_posts'>[], Error> {
  return useBlogSupplementalPosts(options);
}

export function useBlogPost(id: string): UseQueryResult<Tables<'blog_posts'>, Error> {
  return useQuery({
    queryKey: queryKeys.blogPosts.byId(id),
    queryFn: () => fetchBlogPost(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 15,
  });
}

// Medical Procedures
export function useProcedures(
  orderBy: string | undefined = undefined,
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'medical_procedures'>[], Error> {
  const queryKey = orderBy ? [...queryKeys.procedures.all, orderBy] : queryKeys.procedures.all;
  return useQuery<Tables<'medical_procedures'>[], Error>({
    queryKey,
    queryFn: async (): Promise<Tables<'medical_procedures'>[]> => {
      return fetchMedicalProcedures(orderBy);
    },
    staleTime: 1000 * 60 * 60,
    ...options,
  });
}

// Treatments page core dataset (treatments only)
export function useTreatmentsCoreData(
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'treatments'>[], Error> {
  return useQuery({
    queryKey: ['treatments', 'page', 'core'],
    queryFn: async (): Promise<Tables<'treatments'>[]> => {
      const TREATMENT_SELECT = [
        'id',
        'unique_code',
        'name',
        'name_translations',
        'korean_name',
        'category',
        'subcategory',
        'medical_procedure_id',
        'image_urls',
        'description',
        'description_translations',
        'cost_estimate_min',
        'cost_estimate_max',
        'clinic_id',
        'dedicated_provider_id',
        'is_dedicated_to_provider',
        'created_at',
        'updated_at',
      ].join(', ');

      const { data, error } = await supabase.from('treatments').select(TREATMENT_SELECT);
      if (error) throw error;
      return data as unknown as Tables<'treatments'>[];
    },
    staleTime: 1000 * 60 * 30,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Treatments page supplemental dataset (procedures + providers)
export function useTreatmentsSupplementalData(
  options: Record<string, unknown> = {}
): UseQueryResult<
  {
    procedures: Tables<'medical_procedures'>[];
    providers: Tables<'providers'>[];
  },
  Error
> {
  return useQuery({
    queryKey: ['treatments', 'page', 'supplemental'],
    queryFn: async () => {
      const PROCEDURE_SELECT = [
        'id',
        'unique_code',
        'name',
        'korean_name',
        'category',
        'subcategory',
        'generic_image_url',
        'general_description',
        'typical_duration_range',
        'typical_recovery_range',
        'is_active',
        'created_at',
        'updated_at',
      ].join(', ');

      const PROVIDER_SELECT = 'id, name, available_treatments';

      const [proceduresResult, providersResult] = await Promise.all([
        supabase.from('medical_procedures').select(PROCEDURE_SELECT),
        supabase.from('providers').select(PROVIDER_SELECT),
      ]);

      if (proceduresResult.error) throw proceduresResult.error;
      if (providersResult.error) throw providersResult.error;

      return {
        procedures:
          proceduresResult.data as unknown as Database['public']['Tables']['medical_procedures']['Row'][],
        providers:
          providersResult.data as unknown as Database['public']['Tables']['providers']['Row'][],
      };
    },
    staleTime: 1000 * 60 * 30,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Backwards-compatible hook name
export function useTreatmentsPageData(options: Record<string, unknown> = {}): UseQueryResult<
  {
    procedures: Tables<'medical_procedures'>[];
    providers: Tables<'providers'>[];
  },
  Error
> {
  return useTreatmentsSupplementalData(options);
}

// Optimized queries for Doctors page - only fetch necessary fields
export function useDoctorsCoreData(
  options: Record<string, unknown> = {}
): UseQueryResult<Partial<Tables<'doctors'>>[], Error> {
  return useQuery<Partial<Tables<'doctors'>>[], Error>({
    queryKey: ['doctors', 'page', 'core'],
    queryFn: async (): Promise<Partial<Tables<'doctors'>>[]> => {
      const DOCTOR_SELECT = [
        'id',
        'name',
        'name_translations',
        'specialty',
        'specialty_translations',
        'description',
        'description_translations',
        'clinic_id',
        'provider_id',
        'selected_clinics',
        'profile_image_url',
        'languages_spoken',
        'created_at',
      ].join(', ');

      const { data, error } = await supabase.from('doctors').select(DOCTOR_SELECT);
      if (error) throw error;

      return data as unknown as Partial<Tables<'doctors'>>[];
    },
    staleTime: 1000 * 60 * 30,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

export function useDoctorsProviderData(
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'providers'>[], Error> {
  return useQuery<Tables<'providers'>[], Error>({
    queryKey: ['doctors', 'page', 'providers'],
    queryFn: async (): Promise<Tables<'providers'>[]> => {
      const PROVIDER_SELECT = [
        'id',
        'name',
        'name_translations',
        'city',
        'district',
        'branches',
        'featured',
        'image_urls',
      ].join(', ');

      const { data, error } = await supabase.from('providers').select(PROVIDER_SELECT);
      if (error) throw error;
      return data as unknown as Tables<'providers'>[];
    },
    staleTime: 1000 * 60 * 30,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

export function useDoctorsPageData(
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'providers'>[], Error> {
  return useDoctorsProviderData(options);
}

// Image SEO
export function useImageSEOMetadataAll(): UseQueryResult<Tables<'image_seo_metadata'>[], Error> {
  return useQuery({
    queryKey: queryKeys.imageSeo.all,
    queryFn: fetchImageSEOMetadata,
    staleTime: 1000 * 60 * 10,
  });
}

export function useImageSEOMetadataMap(
  loaderFn: () => Promise<unknown>
): UseQueryResult<unknown, Error> {
  // Accept a loader function to build the map when needed in components like AdminSEO
  return useQuery({
    queryKey: queryKeys.imageSeo.map,
    queryFn: loaderFn,
    staleTime: 1000 * 60 * 10,
  });
}

export function usePageSEO(
  pageKey: string,
  languageCode: string = 'en'
): UseQueryResult<Tables<'page_seo'> | null, Error> {
  return useQuery({
    queryKey: queryKeys.pageSeo.byPageKey(pageKey, languageCode),
    queryFn: () => fetchPageSEO(pageKey, languageCode),
    enabled: !!pageKey,
    staleTime: 1000 * 60 * 10,
  });
}

// Inquiries
export function useInquiries(
  orderBy: string = '-created_at'
): UseQueryResult<Tables<'inquiries'>[], Error> {
  return useQuery({
    queryKey: [...queryKeys.inquiries.all, orderBy],
    queryFn: () => fetchInquiries(orderBy),
    staleTime: 1000 * 60 * 5,
  });
}

export function useInquiry(id: string): UseQueryResult<Tables<'inquiries'>, Error> {
  return useQuery({
    queryKey: queryKeys.inquiries.byId(id),
    queryFn: () => fetchInquiry(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useFilteredInquiries(
  filters: Record<string, unknown>,
  orderBy: string = '-created_at',
  limit: number | null = null
): UseQueryResult<Tables<'inquiries'>[], Error> {
  return useQuery({
    queryKey: queryKeys.inquiries.filtered({ filters, orderBy, limit }),
    queryFn: () => fetchFilteredInquiries(filters, orderBy, limit),
    enabled: Object.keys(filters).length > 0,
    staleTime: 1000 * 60 * 5,
  });
}

// Translations
export function useTranslations(
  orderBy: string = '-created_at'
): UseQueryResult<Tables<'translations'>[], Error> {
  return useQuery({
    queryKey: [...queryKeys.translations.all, orderBy],
    queryFn: () => fetchTranslations(orderBy),
    staleTime: 1000 * 60 * 15,
  });
}

export function useTranslation(id: string): UseQueryResult<Tables<'translations'>, Error> {
  return useQuery({
    queryKey: queryKeys.translations.byId(id),
    queryFn: () => fetchTranslation(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 15,
  });
}

// Provider Data Loaders (for complex queries)
export function useProvidersForTreatment(
  treatmentId: string
): UseQueryResult<ProvidersForTreatmentResult, Error> {
  return useQuery({
    queryKey: queryKeys.providerData.byTreatment(treatmentId),
    queryFn: async () => {
      return loadProvidersForTreatment(treatmentId);
    },
    enabled: !!treatmentId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useSingleProvider(providerIdPrefix: string): UseQueryResult<
  {
    provider: Tables<'providers'>;
    treatments: Tables<'treatments'>[];
    doctors: Tables<'doctors'>[];
  },
  Error
> {
  return useQuery<
    {
      provider: Tables<'providers'>;
      treatments: Tables<'treatments'>[];
      doctors: Tables<'doctors'>[];
    },
    Error
  >({
    queryKey: queryKeys.providerData.single(providerIdPrefix),
    queryFn: async (): Promise<{
      provider: Tables<'providers'>;
      treatments: Tables<'treatments'>[];
      doctors: Tables<'doctors'>[];
    }> => {
      return (await loadSingleProvider(providerIdPrefix)) as unknown as {
        provider: Tables<'providers'>;
        treatments: Tables<'treatments'>[];
        doctors: Tables<'doctors'>[];
      };
    },
    enabled: !!providerIdPrefix,
    staleTime: 1000 * 60 * 5,
  });
}

export function useProviderRelations(
  providerId: string,
  availableTreatments: string[] = [],
  options: { limitTreatments?: number; limitDoctors?: number } = {}
): UseQueryResult<ProviderRelationsResult, Error> {
  const normalizedNames = Array.isArray(availableTreatments)
    ? availableTreatments
        .filter(name => typeof name === 'string' && name.trim().length > 0)
        .map(name => name.trim().toLowerCase())
        .sort()
    : [];

  const treatmentKey = normalizedNames.join('|');

  return useQuery({
    queryKey: queryKeys.providerData.relations(providerId, treatmentKey, options),
    queryFn: async () => {
      return loadProviderRelations(providerId, availableTreatments, options);
    },
    enabled: !!providerId,
    staleTime: 1000 * 60 * 5,
  });
}

export function usePreviewProvider(previewId: string): UseQueryResult<
  {
    provider: PreviewProviderData;
    treatments: unknown[];
    doctors: unknown[];
  },
  Error
> {
  return useQuery({
    queryKey: queryKeys.providerData.preview(previewId),
    queryFn: () => {
      return loadPreviewProvider(previewId);
    },
    enabled: !!previewId,
    staleTime: 1000 * 60 * 1, // Preview data is more dynamic
    refetchOnWindowFocus: false, // Disable refetch on focus to prevent errors when sessionStorage is cleared
    refetchOnReconnect: false, // Disable refetch on reconnect for the same reason
  });
}

// Doctors by clinic
export function useDoctorsByClinic(
  clinicId: string
): UseQueryResult<(Omit<Tables<'doctors'>, 'specialty'> & { specialty: string | null })[], Error> {
  return useQuery({
    queryKey: queryKeys.doctors.byClinic(clinicId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('doctors')
        .select('*')
        .or(`provider_id.eq.${clinicId},clinic_id.eq.${clinicId},selected_clinics.cs.{${clinicId}}`)
        .order('display_order', { ascending: true });
      if (error) throw error;
      // Transform specialty from object to string if needed
      return data.map((doctor: Tables<'doctors'>) => ({
        ...doctor,
        specialty:
          typeof doctor.specialty === 'object' && 'text' in doctor.specialty
            ? String((doctor.specialty as { text: string }).text)
            : String(doctor.specialty),
      }));
    },
    enabled: !!clinicId,
    staleTime: 1000 * 60 * 5,
  });
}

// Treatments by clinic
export function useTreatmentsByClinic(
  clinicId: string
): UseQueryResult<Partial<Tables<'treatments'>>[], Error> {
  return useQuery({
    queryKey: queryKeys.treatments.byClinic(clinicId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('treatments')
        .select('id, unique_code, name, description, image_urls')
        .or(`clinic_id.eq.${clinicId},dedicated_provider_id.eq.${clinicId}`);
      if (error) throw error;
      return data;
    },
    enabled: !!clinicId,
    staleTime: 1000 * 60 * 5,
  });
}

// Provider ordering preference
export function useProviderOrderingPreference(clinicId: string): UseQueryResult<boolean, Error> {
  return useQuery({
    queryKey: queryKeys.providers.orderingPreference(clinicId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('providers')
        .select('use_manual_doctor_order')
        .eq('id', clinicId)
        .single();
      if (error) throw error;
      return !!data.use_manual_doctor_order;
    },
    enabled: !!clinicId,
    staleTime: 1000 * 60 * 15,
  });
}

// Doctor by ID or prefix (for detail page)
const doctorPrefixCache = new Map<string, string>();

const normalizeDoctorRecord = (doctor: Tables<'doctors'>) => ({
  ...doctor,
  specialty:
    typeof doctor.specialty === 'object' && 'text' in doctor.specialty
      ? String((doctor.specialty as { text: string }).text)
      : String(doctor.specialty),
});

const formatUuidValue = (value: string) => {
  if (!value) return null;

  // Check for standard UUID format with hyphens
  if (/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value)) {
    return value;
  }
  // Check for UUID without hyphens (32 characters)
  if (/^[0-9a-f]{32}$/i.test(value)) {
    return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
  }
  return null;
};

const processSupabaseResponse = (
  result: PostgrestSingleResponse<Tables<'doctors'> | null> | PostgrestResponse<Tables<'doctors'>[]>
): Tables<'doctors'> | null => {
  if (result && typeof result === 'object' && 'error' in result) {
    const { data, error } = result as {
      data: Tables<'doctors'> | Tables<'doctors'>[] | null;
      error: PostgrestError | null;
    };
    if (error) {
      if (error.code !== 'PGRST116') {
        // Doctor lookup failed
      }
      return null;
    }
    if (!data) {
      return null;
    }
    if (Array.isArray(data)) {
      return data.length > 0 ? data[0]! : null;
    }
    return data;
  }
  return null;
};

const processDirectDoctorData = (result: unknown): Tables<'doctors'> | null => {
  if (result && typeof result === 'object' && 'specialty' in result) {
    return result as Tables<'doctors'>;
  }
  return null;
};

const runDoctorLookup = async (
  lookupFn: () => Promise<
    PostgrestSingleResponse<Tables<'doctors'> | null> | PostgrestResponse<Tables<'doctors'>[]>
  >,
  _context: string
): Promise<Tables<'doctors'> | null> => {
  try {
    const result = await lookupFn();

    // Handle Supabase response format
    const supabaseResult = processSupabaseResponse(result);
    if (supabaseResult !== null) {
      return supabaseResult;
    }

    // Handle direct data return (for normalizeDoctorRecord results)
    return processDirectDoctorData(result);
  } catch {
    return null;
  }
};

const resolveDoctorRecord = (doctorRecord: Tables<'doctors'>, lowercaseIdentifier: string) => {
  if (lowercaseIdentifier.length === 8) {
    doctorPrefixCache.set(lowercaseIdentifier, doctorRecord.id);
  }
  return normalizeDoctorRecord(doctorRecord);
};

const findDoctorByPrefix = async (
  supabaseClient: typeof supabase,
  prefix: string,
  limit: number
) => {
  const { data, error } = await supabase
    .from('doctors')
    .select('id')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  if (data.length === 0) return null;

  const matched = data.find((doc: { id: string }) =>
    String(doc.id).toLowerCase().startsWith(prefix.toLowerCase())
  );
  if (!matched?.id) return null;

  const { data: fullDoctor, error: fullErr } = await supabase
    .from('doctors')
    .select('*')
    .eq('id', matched.id)
    .maybeSingle();

  if (fullErr) throw fullErr;
  return fullDoctor;
};

// Helper function to lookup doctor from cache
const lookupDoctorFromCache = async (
  lowercaseIdentifier: string
): Promise<ReturnType<typeof normalizeDoctorRecord> | null> => {
  const cachedDoctorId = doctorPrefixCache.get(lowercaseIdentifier);
  if (!cachedDoctorId) return null;

  const cachedDoctor = await runDoctorLookup(
    async () => await supabase.from('doctors').select('*').eq('id', cachedDoctorId).maybeSingle(),
    'cache_hit'
  );

  if (cachedDoctor) {
    return resolveDoctorRecord(cachedDoctor, lowercaseIdentifier);
  }

  doctorPrefixCache.delete(lowercaseIdentifier);
  return null;
};

// Helper function to build primary lookup queries
const buildPrimaryLookups = (identifier: string, normalizedUuid: string | null) => {
  const primaryLookups = [
    async () =>
      await supabase.from('doctors').select('*').eq('unique_code', identifier).maybeSingle(),
  ];

  if (normalizedUuid) {
    primaryLookups.unshift(
      async () => await supabase.from('doctors').select('*').eq('id', normalizedUuid).maybeSingle()
    );
  }

  if (identifier.length >= 4) {
    primaryLookups.push(
      async () =>
        await supabase
          .from('doctors')
          .select('*')
          .ilike('unique_code', `${identifier}%`)
          .order('updated_at', { ascending: false })
          .maybeSingle()
    );
  }

  return primaryLookups;
};

// Helper function to try primary lookups
const tryPrimaryLookups = async (
  primaryLookups: Array<
    () => Promise<
      PostgrestSingleResponse<Tables<'doctors'> | null> | PostgrestResponse<Tables<'doctors'>[]>
    >
  >,
  lowercaseIdentifier: string
): Promise<ReturnType<typeof normalizeDoctorRecord> | null> => {
  const primaryResults = await Promise.all(
    primaryLookups.map((lookup, index) => runDoctorLookup(lookup, `primary_${index}`))
  );

  for (const result of primaryResults) {
    if (result) {
      return resolveDoctorRecord(result, lowercaseIdentifier);
    }
  }

  return null;
};

// Helper function to try prefix lookups for 8-character identifiers
const tryPrefixLookups = async (
  identifier: string,
  lowercaseIdentifier: string
): Promise<ReturnType<typeof normalizeDoctorRecord> | null> => {
  if (identifier.length !== 8) return null;

  const doctorFromRecent = await findDoctorByPrefix(supabase, lowercaseIdentifier, 150);
  if (doctorFromRecent) {
    return resolveDoctorRecord(doctorFromRecent, lowercaseIdentifier);
  }

  const doctorFromExtended = await findDoctorByPrefix(supabase, lowercaseIdentifier, 1000);
  if (doctorFromExtended) {
    return resolveDoctorRecord(doctorFromExtended, lowercaseIdentifier);
  }

  throw new Error('No doctor found matching prefix');
};

export function useDoctorByIdOrPrefix(
  doctorId: string | null
): UseQueryResult<ReturnType<typeof normalizeDoctorRecord> | null, Error> {
  return useQuery({
    queryKey: queryKeys.doctors.byIdOrPrefix(String(doctorId)),
    queryFn: async () => {
      if (!doctorId) {
        throw new Error('Doctor ID not provided');
      }

      const identifier = String(doctorId).trim();
      const lowercaseIdentifier = identifier.toLowerCase();

      // Try cache lookup first
      const cachedResult = await lookupDoctorFromCache(lowercaseIdentifier);
      if (cachedResult) return cachedResult;

      const normalizedUuid = formatUuidValue(identifier);
      const primaryLookups = buildPrimaryLookups(identifier, normalizedUuid);

      // Try primary lookups
      const primaryResult = await tryPrimaryLookups(primaryLookups, lowercaseIdentifier);
      if (primaryResult) return primaryResult;

      // Try prefix lookups for exact length matches
      const prefixResult = await tryPrefixLookups(identifier, lowercaseIdentifier);
      if (prefixResult) return prefixResult;

      throw new Error('Doctor not found');
    },
    enabled: !!doctorId,
    staleTime: 1000 * 60 * 5,
  });
}

// Providers by clinic IDs
export function useProvidersByClinicIds(
  clinicIds: string[]
): UseQueryResult<Tables<'providers'>[], Error> {
  return useQuery({
    queryKey: queryKeys.providers.byClinicIds(clinicIds),
    queryFn: async () => {
      const { data, error } = await supabase.from('providers').select('*').in('id', clinicIds);
      if (error) throw error;
      return data;
    },
    enabled: clinicIds.length > 0,
    staleTime: 1000 * 60 * 5,
  });
}

// Treatments by provider IDs (for providers page)
export function useTreatmentsByProviderIds(
  providerIds: string[],
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'treatments'>[], Error> {
  return useQuery({
    queryKey: queryKeys.treatments.byProviderIds(providerIds),
    queryFn: async () => {
      const orConditions = providerIds
        .map(id => `clinic_id.eq.${id},dedicated_provider_id.eq.${id}`)
        .join(',');
      const { data, error } = await supabase.from('treatments').select('*').or(orConditions);
      if (error) throw error;
      return data;
    },
    enabled: providerIds.length > 0,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Treatments by names (for available_treatments arrays)
export function useTreatmentsByNames(
  treatmentNames: string[],
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'treatments'>[], Error> {
  return useQuery<Tables<'treatments'>[], Error>({
    queryKey: queryKeys.treatments.byNames(treatmentNames),
    queryFn: async (): Promise<Tables<'treatments'>[]> => {
      return fetchTreatmentsByNames(supabase, treatmentNames);
    },
    enabled: treatmentNames.length > 0,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Doctors by provider IDs - optimized to fetch only necessary fields
export function useDoctorsByProviderIds(
  providerIds: string[],
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'doctors'>[], Error> {
  return useQuery<Tables<'doctors'>[], Error>({
    queryKey: queryKeys.doctors.byProviderIds(providerIds),
    queryFn: async (): Promise<Tables<'doctors'>[]> => {
      // Only select fields needed for display
      const DOCTOR_SELECT = [
        'id',
        'name',
        'name_translations',
        'clinic_id',
        'provider_id',
        'selected_clinics',
        'profile_image_url',
        'specialty',
        'specialty_translations',
        'social_links',
      ].join(', ');
      const orConditions = providerIds
        .map((id: string) => `clinic_id.eq.${id},provider_id.eq.${id},selected_clinics.cs.{${id}}`)
        .join(',');
      const { data, error } = await supabase.from('doctors').select(DOCTOR_SELECT).or(orConditions);
      if (error) throw error;
      // Return empty array if no doctors found - don't fetch all doctors as fallback
      return data as unknown as Tables<'doctors'>[];
    },
    enabled: providerIds.length > 0,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: false, // Change to false to leverage cache better
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Medical procedures by IDs
export function useMedicalProceduresByIds(
  procedureIds: string[],
  options: Record<string, unknown> = {}
): UseQueryResult<Tables<'medical_procedures'>[], Error> {
  return useQuery({
    queryKey: queryKeys.procedures.byIds(procedureIds),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('medical_procedures')
        .select('*')
        .in('id', procedureIds);
      if (error) throw error;
      return data;
    },
    enabled: procedureIds.length > 0,
    staleTime: 1000 * 60 * 60,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Featured providers - fetches all featured providers for rotation
export function useFeaturedProviders(options = {}): UseQueryResult<Tables<'providers'>[], Error> {
  return useQuery({
    queryKey: queryKeys.providers.featured(1000),
    queryFn: async () => {
      // Fetch featured providers, fallback to recent providers if none are featured
      try {
        const result = await Provider.filter({ featured: true }, '-updated_at', 10);
        return result;
      } catch (error) {
        console.error('useFeaturedProviders: Query failed:', error);
        // Return empty array instead of throwing to prevent app crash
        return [];
      }
    },
    staleTime: 1000 * 60 * 15, // 15 minutes cache
    refetchOnMount: true, // Enable refetch on mount to get latest featured providers
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Providers page core dataset (providers only)
export function useProvidersCoreData(
  options: Record<string, unknown> = {}
): UseQueryResult<Partial<Tables<'providers'>>[], Error> {
  return useQuery({
    queryKey: ['providers', 'page', 'core'],
    queryFn: async () => {
      const PROVIDER_SELECT = [
        'id',
        'name',
        'name_translations',
        'description',
        'description_translations',
        'city',
        'district',
        'location',
        'specialties',
        'facility_type',
        'featured',
        'created_at',
        'is_branch_provider',
        'branches',
        'available_treatments',
        'languages_spoken',
        'image_urls',
        'operating_hours',
        'hide_operating_hours',
        'contact_phone',
        'verified',
        'update_completed',
        'updated_at',
      ].join(', ');

      const { data, error } = await supabase.from('providers').select(PROVIDER_SELECT);
      if (error) throw error;
      return (data as Partial<Tables<'providers'>>[]).map(
        (provider: Partial<Tables<'providers'>>) => ({
          ...provider,
          phone: provider.contact_phone,
        })
      );
    },
    staleTime: 1000 * 60 * 5,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Providers page supplemental dataset (doctors + treatments)
export function useProvidersSupplementalData(options = {}): UseQueryResult<
  {
    doctors: Tables<'doctors'>[];
    treatments: Tables<'treatments'>[];
  },
  Error
> {
  return useQuery({
    queryKey: ['providers', 'page', 'supplemental'],
    queryFn: async () => {
      const DOCTOR_SELECT = [
        'id',
        'name',
        'name_translations',
        'clinic_id',
        'provider_id',
        'selected_clinics',
        'profile_image_url',
        'specialty',
        'specialty_translations',
      ].join(', ');

      const TREATMENT_SELECT = [
        'id',
        'name',
        'unique_code',
        'clinic_id',
        'dedicated_provider_id',
        'medical_procedure_id',
        'image_urls',
      ].join(', ');

      const [doctorsResult, treatmentsResult] = await Promise.all([
        supabase.from('doctors').select(DOCTOR_SELECT),
        supabase.from('treatments').select(TREATMENT_SELECT),
      ]);

      if (doctorsResult.error) throw doctorsResult.error;
      if (treatmentsResult.error) throw treatmentsResult.error;

      return {
        doctors: doctorsResult.data as unknown as Database['public']['Tables']['doctors']['Row'][],
        treatments:
          treatmentsResult.data as unknown as Database['public']['Tables']['treatments']['Row'][],
      };
    },
    staleTime: 1000 * 60 * 5,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Backwards-compatible hook name
export function useProvidersPageData(options = {}): UseQueryResult<
  {
    doctors: Tables<'doctors'>[];
    treatments: Tables<'treatments'>[];
  },
  Error
> {
  return useProvidersSupplementalData(options);
}

// Provider equipment by IDs
export function useProviderEquipmentByIds(
  providerIds: string[],
  options: Record<string, unknown> = {}
): UseQueryResult<ProviderEquipmentWithDetails[], Error> {
  const { enabled = true, ...rest } = options;
  const normalizedIds = Array.isArray(providerIds)
    ? [
        ...new Set(
          providerIds.map(id => (id.trim() === '' ? null : id)).filter(Boolean) as string[]
        ),
      ]
    : [];
  const keyPart = normalizedIds.map(String).sort().join(',');
  const shouldFetch = enabled && normalizedIds.length > 0;

  return useQuery({
    queryKey: ['provider_equipment', keyPart],
    enabled: !!shouldFetch,
    queryFn: async () => {
      if (!normalizedIds.length) return [];

      const equipmentLists = await Promise.all(
        normalizedIds.map(async providerId => {
          try {
            const entries = await (
              ProviderEquipment as ProviderEquipmentWithCustomMethods
            ).getByProvider(providerId);
            return Array.isArray(entries) ? entries : [];
          } catch {
            return [];
          }
        })
      );

      const merged = equipmentLists.flat();
      return merged.sort((a, b) => {
        const aValue = a.displayPriority ?? a.display_priority;
        const bValue = b.displayPriority ?? b.display_priority;
        const aPriority = aValue != null ? Number(aValue) : 0;
        const bPriority = bValue != null ? Number(bValue) : 0;
        const priorityDiff = aPriority - bPriority;
        if (priorityDiff !== 0) return priorityDiff;

        const aName = typeof a.equipment.name === 'string' ? a.equipment.name : '';
        const bName = typeof b.equipment.name === 'string' ? b.equipment.name : '';
        return aName.toLowerCase().localeCompare(bName.toLowerCase());
      });
    },
    staleTime: 1000 * 60 * 5,
    ...rest,
  });
}

// Home page treatments (limited)
export function useHomeTreatments(
  limit = 40,
  options = {}
): UseQueryResult<Tables<'treatments'>[], Error> {
  return useQuery<Tables<'treatments'>[], Error>({
    queryKey: queryKeys.treatments.home(limit),
    queryFn: async (): Promise<Tables<'treatments'>[]> => {
      const TREATMENT_HOME_SELECT = [
        'id',
        'unique_code',
        'name',
        'name_translations',
        'korean_name',
        'category',
        'subcategory',
        'medical_procedure_id',
        'image_urls',
        'description',
        'description_translations',
        'cost_estimate_min',
        'cost_estimate_max',
        'duration_type',
        'duration_unit',
        'duration_value',
        'duration_range_min',
        'duration_range_max',
        'recovery_type',
        'recovery_unit',
        'recovery_value',
        'recovery_range_min',
        'recovery_range_max',
        'anesthesia_required',
        'treatment_types',
        'treatment_types_translations',
        'created_at',
      ].join(', ');
      const { data, error } = await supabase
        .from('treatments')
        .select(TREATMENT_HOME_SELECT)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data as unknown as Tables<'treatments'>[];
    },
    staleTime: 1000 * 60 * 15,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Home page medical procedures (limited)
export function useHomeProcedures(
  limit = 30,
  options = {}
): UseQueryResult<(Tables<'medical_procedures'> & { description: string | null })[], Error> {
  return useQuery<(Tables<'medical_procedures'> & { description: string | null })[], Error>({
    queryKey: queryKeys.procedures.home(limit),
    queryFn: async (): Promise<
      (Tables<'medical_procedures'> & { description: string | null })[]
    > => {
      const PROCEDURE_HOME_SELECT = [
        'id',
        'unique_code',
        'name',
        'korean_name',
        'category',
        'subcategory',
        'generic_image_url',
        'general_description',
        'created_at',
      ].join(', ');
      const { data, error } = await supabase
        .from('medical_procedures')
        .select(PROCEDURE_HOME_SELECT)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      const rows = data as unknown as Tables<'medical_procedures'>[];
      return rows.map(
        (procedure: Tables<'medical_procedures'>) =>
          ({
            ...procedure,
            description: procedure.general_description,
          }) as Tables<'medical_procedures'> & { description: string | null }
      );
    },
    staleTime: 1000 * 60 * 15,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Linked treatments for doctor
export function useLinkedTreatmentsForDoctor(
  linkedTreatmentCodes: string[] | undefined | null
): UseQueryResult<Tables<'treatments'>[], Error> {
  const safeLinkedTreatmentCodes = Array.isArray(linkedTreatmentCodes) ? linkedTreatmentCodes : [];

  return useQuery({
    queryKey: queryKeys.treatments.linked(safeLinkedTreatmentCodes),
    queryFn: async () => {
      const uniqueCodes = [
        ...new Set(
          safeLinkedTreatmentCodes
            .map((code: string) => (typeof code === 'string' ? code.trim() : ''))
            .filter(Boolean)
        ),
      ];

      if (uniqueCodes.length === 0) return [];

      const { data, error } = await supabase
        .from('treatments')
        .select(
          'id, unique_code, name, name_translations, description, description_translations, image_urls'
        )
        .in('unique_code', uniqueCodes);
      if (error) throw error;
      const treatmentsData = data;

      const treatmentsByCode = new Map();
      for (const treatment of treatmentsData) {
        if (treatment.unique_code) {
          treatmentsByCode.set(treatment.unique_code, treatment);
        }
        if (treatment.id && !treatmentsByCode.has(treatment.id)) {
          treatmentsByCode.set(treatment.id, treatment);
        }
      }

      return uniqueCodes
        .map(code => treatmentsByCode.get(code) as Tables<'treatments'> | undefined)
        .filter((treatment): treatment is Tables<'treatments'> => treatment != null);
    },
    enabled: safeLinkedTreatmentCodes.length > 0,
    staleTime: 1000 * 60 * 5,
  });
}

// Helper function to get user session
const getUserSession = async () => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();
  return { session, sessionError };
};

// Helper function to fetch profile and roles
const fetchProfileAndRoles = async (userId: string) => {
  const [{ data: profile, error: profileError }, { data: roles, error: rolesError }] =
    await Promise.all([
      supabase.from('profiles').select('*').eq('id', userId).single(),
      supabase.from('user_roles').select('role').eq('user_id', userId),
    ]);
  return { profile, profileError, roles, rolesError };
};

// Helper function to determine user role
const determineUserRole = (userRoles: string[], isPartner: boolean) => {
  if (userRoles.includes('admin')) return 'admin';
  if (userRoles.includes('partner') || isPartner) return 'partner';
  return 'user';
};

// Helper function to construct medical profile data
const constructMedicalProfile = (profileData: Tables<'profiles'>) => ({
  height_cm: String(profileData.height_cm ?? ''),
  weight_kg: String(profileData.weight_kg ?? ''),
  blood_type: profileData.blood_type ?? '',
  family_history: profileData.family_history ?? '',
  smoking_status: profileData.smoking_status ?? '',
  alcohol_consumption: profileData.alcohol_consumption ?? '',
  exercise_frequency: profileData.exercise_frequency ?? '',
  allergies: profileData.allergies ?? [],
  current_medications: profileData.current_medications ?? [],
  past_surgeries: profileData.past_surgeries ?? [],
  chronic_conditions: profileData.chronic_conditions ?? [],
});

// Helper function to construct user profile object
const constructUserProfile = (
  session: Session,
  profile: Tables<'profiles'>,
  userRoles: string[]
) => {
  const profileData = profile;
  const isPartner = profile.is_partner ?? userRoles.includes('partner');
  const medicalProfile = constructMedicalProfile(profileData);

  return {
    id: session.user.id,
    email: session.user.email ?? '',
    full_name: profileData.full_name,
    phone: profileData.phone,
    age: String(profileData.age ?? ''),
    country: profileData.country ?? '',
    gender: profileData.gender ?? '',
    profile_image_url: profileData.profile_image_url ?? '',
    preferred_languages: profileData.preferred_languages ?? [],
    emergency_contact_name: profileData.emergency_contact_name ?? '',
    emergency_contact_phone: profileData.emergency_contact_phone ?? '',
    emergency_contact_relationship: profileData.emergency_contact_relationship ?? '',
    ...medicalProfile,
    created_at: profile.created_at ?? session.user.created_at,
    profile_completed: profile.profile_completed ?? false,
    is_partner: isPartner,
    is_admin: userRoles.includes('admin'),
    role: determineUserRole(userRoles, isPartner),
    partner_category: profile.partner_category ?? null,
    partner_permissions: profile.partner_permissions ?? null,
    assigned_provider_ids: profile.assigned_provider_ids ?? [],
  };
};

// User profile and session data
export const fetchUserProfile = async () => {
  const { session, sessionError } = await getUserSession();

  if (sessionError || !session) {
    return null;
  }

  const { profile, profileError, roles, rolesError } = await fetchProfileAndRoles(session.user.id);

  if (profileError) {
    throw profileError;
  }

  if (!profile) {
    return null;
  }

  if (rolesError) {
    // Roles fetch error handled silently
  }

  const userRoles = roles?.map(r => r.role) ?? [];
  return constructUserProfile(session, profile, userRoles);
};

// Standalone fetch functions for prefetching
export const fetchProviders = async (): Promise<Tables<'providers'>[]> => {
  return Provider.list();
};

export const fetchProvider = async (id: string): Promise<Tables<'providers'>> => {
  return Provider.get(id);
};

export const fetchInquiries = async (orderBy = '-created_at') => {
  return Inquiry.list(orderBy);
};

export const fetchEquipmentList = async (): Promise<EquipmentType[]> => {
  return await Equipment.list();
};

export const fetchTreatments = async (
  orderBy: string | undefined = undefined
): Promise<Tables<'treatments'>[]> => {
  return await Treatment.list(orderBy);
};

export const fetchTreatment = async (id: string) => {
  return Treatment.get(id);
};

export const fetchDoctors = async (): Promise<Tables<'doctors'>[]> => {
  return await Doctor.list();
};

export const fetchDoctor = async (id: string) => {
  return Doctor.get(id);
};

export const fetchBlogPosts = async (
  options: { published?: boolean; orderBy?: string; limit?: number; fields?: string[] } = {}
): Promise<Tables<'blog_posts'>[]> => {
  const { published, orderBy = '-created_at', limit, fields } = options;
  if (fields) {
    return await BlogPost.list({ fields });
  }
  if (published) {
    return await BlogPost.filter({ published: true }, orderBy, limit);
  }
  // By default, filter out draft posts (only show published posts)
  return await BlogPost.filter({ published: true }, orderBy, limit);
};

export const fetchBlogPost = async (id: string) => {
  // Load all published blog posts and find by ID
  const allPosts = await BlogPost.filter({ published: true });
  const postsArray = Array.isArray(allPosts) ? allPosts : [];
  const foundPost = postsArray.find(
    (p: Tables<'blog_posts'>) => String(p.id) === String(id) || p.id.slice(0, 8) === id
  );
  if (!foundPost) throw new Error('Blog post not found');
  return foundPost;
};

export const fetchMedicalProcedures = async (
  orderBy: string | undefined = undefined
): Promise<Tables<'medical_procedures'>[]> => {
  const result = await MedicalProcedure.list(orderBy);
  if (Array.isArray(result)) {
    return result;
  }
  // If it's not an array, it might be an error, return empty array
  return [];
};

export const fetchImageSEOMetadata = async () => {
  return ImageSEOMetadata.list();
};

export const fetchPageSEO = async (
  pageKey: string,
  languageCode: string = 'en'
): Promise<Tables<'page_seo'> | null> => {
  return (PageSEO as PageSEOWithCustomMethods).getByPageKey(pageKey, languageCode);
};

export const fetchInquiry = async (id: string) => {
  return Inquiry.get(id);
};

export const fetchFilteredInquiries = async (
  filters: Record<string, unknown>,
  orderBy: string = '-created_at',
  limit: number | null = null
) => {
  return Inquiry.filter(filters, orderBy, limit);
};

export const fetchTranslations = async (orderBy = '-created_at') => {
  return Translation.list(orderBy);
};

export const fetchTranslation = async (id: string) => {
  return Translation.get(id);
};

export function useUserProfile(): UseQueryResult<User | null, Error> {
  return useQuery({
    queryKey: queryKeys.user.profile,
    queryFn: fetchUserProfile,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
}

// Mutations helpers
export function useProviderMutations(): {
  create: UseMutationResult<Tables<'providers'>, Error, TablesInsert<'providers'>>;
  update: UseMutationResult<
    Tables<'providers'>,
    Error,
    { id: string | number; data: TablesUpdate<'providers'> }
  >;
  remove: UseMutationResult<{ success: boolean }, Error, string | number>;
} {
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: async (data: TablesInsert<'providers'>): Promise<Tables<'providers'>> => {
      return await Provider.create(data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.providers.all });
    },
  });
  const update = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string | number;
      data: TablesUpdate<'providers'>;
    }): Promise<Tables<'providers'>> => {
      return await Provider.update(id, data);
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.providers.all });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.providers.byId(String(variables.id)),
      });
    },
  });
  const remove = useMutation({
    mutationFn: async (id: string | number): Promise<{ success: boolean }> => {
      return await Provider.delete(id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.providers.all });
    },
  });
  return { create, update, remove };
}

export function useInquiryMutations(): {
  create: UseMutationResult<Tables<'inquiries'>, Error, TablesInsert<'inquiries'>>;
  update: UseMutationResult<
    Tables<'inquiries'>,
    Error,
    { id: string | number; data: TablesUpdate<'inquiries'> }
  >;
  remove: UseMutationResult<{ success: boolean }, Error, string | number>;
} {
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: (data: TablesInsert<'inquiries'>) => Inquiry.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inquiries.all });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: TablesUpdate<'inquiries'> }) =>
      Inquiry.update(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inquiries.all });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.inquiries.byId(String(variables.id)),
      });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string | number) => Inquiry.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inquiries.all });
    },
  });
  return { create, update, remove };
}

export function useTranslationMutations(): {
  create: UseMutationResult<Tables<'translations'>, Error, TablesInsert<'translations'>>;
  update: UseMutationResult<
    Tables<'translations'>,
    Error,
    { id: string | number; data: TablesUpdate<'translations'> }
  >;
  remove: UseMutationResult<{ success: boolean }, Error, string | number>;
} {
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: (data: TablesInsert<'translations'>) => Translation.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.translations.all });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: TablesUpdate<'translations'> }) =>
      Translation.update(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.translations.all });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.translations.byId(String(variables.id)),
      });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string | number) => Translation.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.translations.all });
    },
  });
  return { create, update, remove };
}

export function useTreatmentMutations(): {
  create: UseMutationResult<Tables<'treatments'>, Error, TablesInsert<'treatments'>>;
  update: UseMutationResult<
    Tables<'treatments'>,
    Error,
    { id: string | number; data: TablesUpdate<'treatments'> }
  >;
  remove: UseMutationResult<{ success: boolean }, Error, string | number>;
} {
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: (data: TablesInsert<'treatments'>) => Treatment.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.treatments.all });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: TablesUpdate<'treatments'> }) =>
      Treatment.update(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.treatments.all });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.treatments.byId(String(variables.id)),
      });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string | number) => Treatment.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.treatments.all });
    },
  });
  return { create, update, remove };
}

// Profile mutations
export function useProfileMutations(): {
  update: UseMutationResult<unknown, Error, { userId: string; data: Record<string, unknown> }>;
} {
  const queryClient = useQueryClient();
  const update = useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: Record<string, unknown> }) => {
      const { data: result, error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', userId)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate user profile queries
      void queryClient.invalidateQueries({ queryKey: queryKeys.user.profile });
      void queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
      void queryClient.invalidateQueries({ queryKey: ['user', 'profile', variables.userId] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'profile', variables.userId] });
    },
  });
  return { update };
}

// Doctor mutations
export function useDoctorMutations(): {
  create: UseMutationResult<Tables<'doctors'>, Error, TablesInsert<'doctors'>>;
  update: UseMutationResult<
    Tables<'doctors'>,
    Error,
    { id: string | number; data: TablesUpdate<'doctors'> }
  >;
  remove: UseMutationResult<{ success: boolean }, Error, string | number>;
} {
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: (data: TablesInsert<'doctors'>) => Doctor.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.doctors.all });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: TablesUpdate<'doctors'> }) =>
      Doctor.update(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.doctors.all,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.doctors.byId(String(variables.id)),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.doctors.byIdOrPrefix(String(variables.id)),
      });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string | number) => Doctor.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.doctors.all });
    },
  });
  return { create, update, remove };
}

// Medical Procedure mutations
export function useProcedureMutations(): {
  create: UseMutationResult<
    Tables<'medical_procedures'>,
    Error,
    TablesInsert<'medical_procedures'>
  >;
  update: UseMutationResult<
    Tables<'medical_procedures'>,
    Error,
    { id: string | number; data: Record<string, unknown> }
  >;
  remove: UseMutationResult<{ success: boolean }, Error, string | number>;
} {
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: async (data: TablesInsert<'medical_procedures'>) => {
      const { data: result, error } = await supabase
        .from('medical_procedures')
        .insert([data])
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.procedures.all });
    },
  });
  const update = useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: Record<string, unknown> }) => {
      const { data: result, error } = await supabase
        .from('medical_procedures')
        .update(data)
        .eq('id', String(id))
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.procedures.all });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.procedures.byId(String(variables.id)),
      });
    },
  });
  const remove = useMutation({
    mutationFn: async (id: string | number) => {
      const { error } = await supabase.from('medical_procedures').delete().eq('id', String(id));
      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.procedures.all });
    },
  });
  return { create, update, remove };
}

// Image SEO Metadata mutations
export function useImageSEOMutations(): {
  create: UseMutationResult<
    Tables<'image_seo_metadata'>,
    Error,
    TablesInsert<'image_seo_metadata'>
  >;
  update: UseMutationResult<
    Tables<'image_seo_metadata'>,
    Error,
    { id: string | number; data: TablesUpdate<'image_seo_metadata'> }
  >;
  remove: UseMutationResult<{ success: boolean }, Error, string | number>;
} {
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: (data: TablesInsert<'image_seo_metadata'>) => ImageSEOMetadata.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.imageSeo.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.imageSeo.map });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: TablesUpdate<'image_seo_metadata'> }) =>
      ImageSEOMetadata.update(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.imageSeo.all,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.imageSeo.map,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.imageSeo.byId(String(variables.id)),
      });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string | number) => ImageSEOMetadata.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.imageSeo.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.imageSeo.map });
    },
  });
  return { create, update, remove };
}

export function useEquipmentMutations(): {
  create: UseMutationResult<EquipmentType, Error, TablesInsert<'equipment'>>;
  update: UseMutationResult<
    EquipmentType,
    Error,
    { id: string | number; data: TablesUpdate<'equipment'> }
  >;
  remove: UseMutationResult<{ success: boolean }, Error, string | number>;
} {
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: (data: TablesInsert<'equipment'>) => Equipment.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.equipment.all });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: TablesUpdate<'equipment'> }) =>
      Equipment.update(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.equipment.all });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.byId(String(variables.id)),
      });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string | number) => Equipment.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.equipment.all });
    },
  });
  return { create, update, remove };
}

// Admin Statistics

export const fetchAdminStats = async () => {
  const [
    providersCountResult,
    doctorsResult,
    proceduresResult,
    treatmentsResult,
    inquiriesResult,
    reportsResult,
    blogPostsResult,
    partnersResult,
    usersResult,
    equipmentResult,
  ] = await Promise.all([
    supabase.from('providers').select('id', { count: 'exact', head: true }),
    supabase.from('doctors').select('id', { count: 'exact', head: true }),
    supabase.from('medical_procedures').select('id', { count: 'exact', head: true }),
    supabase.from('treatments').select('id', { count: 'exact', head: true }),
    supabase.from('inquiries').select('id', { count: 'exact', head: true }),
    supabase.from('reports').select('id', { count: 'exact', head: true }),
    supabase.from('blog_posts').select('id', { count: 'exact', head: true }),
    supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('is_partner', true),
    supabase.from('profiles').select('id', { count: 'exact', head: true }),
    supabase.from('equipment').select('id', { count: 'exact', head: true }),
  ]);
  return {
    totalProviders: providersCountResult.count ?? 0,
    totalDoctors: doctorsResult.count ?? 0,
    totalProcedures: proceduresResult.count ?? 0,
    totalTreatments: treatmentsResult.count ?? 0,
    totalInquiries: inquiriesResult.count ?? 0,
    totalReports: reportsResult.count ?? 0,
    totalBlogPosts: blogPostsResult.count ?? 0,
    totalPartners: partnersResult.count ?? 0,
    totalUsers: usersResult.count ?? 0,
    totalEquipments: equipmentResult.count ?? 0,
  };
};

export function useAdminStats(): UseQueryResult<unknown, Error> {
  return useQuery({
    queryKey: queryKeys.admin.stats,
    queryFn: fetchAdminStats,
    staleTime: 1000 * 60 * 5,
  });
}

export const fetchProvidersWithTodos = async () => {
  const { data, error } = await supabase.from('providers').select('id, name, city, admin_todos');
  if (error) throw error;
  const safeProviders = data;
  const providersWithTodos = safeProviders
    .map(provider => ({
      ...provider,
      todos: Array.isArray(provider.admin_todos) ? provider.admin_todos : [],
    }))
    .filter(provider => provider.todos.length > 0);
  return {
    providersWithTodos,
    allProviders: safeProviders.map(provider => ({
      id: provider.id,
      name: provider.name,
      city: provider.city,
    })),
    totalTodos: providersWithTodos.reduce(
      (sum, p) => sum + (Array.isArray(p.todos) ? p.todos.length : 0),
      0
    ),
    completedTodos: providersWithTodos.reduce(
      (sum, p) =>
        sum +
        (Array.isArray(p.todos)
          ? p.todos.filter(
              (t: unknown) =>
                typeof t === 'object' &&
                t !== null &&
                (t as Record<string, unknown>).completed === true
            ).length
          : 0),
      0
    ),
  };
};

export function useProvidersWithTodos(): UseQueryResult<
  {
    providersWithTodos: Array<{
      id: string;
      name: string | null;
      city: string | null;
      admin_todos: unknown;
      todos: unknown[];
    }>;
    allProviders: Array<{
      id: string;
      name: string | null;
      city: string | null;
    }>;
    totalTodos: number;
    completedTodos: number;
  },
  Error
> {
  return useQuery({
    queryKey: queryKeys.admin.providersWithTodos,
    queryFn: fetchProvidersWithTodos,
    staleTime: 1000 * 60 * 5,
  });
}

// Page SEO queries
export const fetchPageSEOList = async () => {
  const { groupPagesByKey } = await import('@/components/admin/admin-page-seo/utils');
  const data = await PageSEO.filter({ is_active: true }, 'page_type');
  return groupPagesByKey(data);
};

type GroupedPageSEO = {
  page_key: string;
  page_type: string;
  locales: Record<
    string,
    import('@/integrations/supabase/types').Database['public']['Tables']['page_seo']['Row']
  >;
  displayTitle: string;
};

export function usePageSEOList(): UseQueryResult<GroupedPageSEO[], Error> {
  return useQuery({
    queryKey: ['pageSeo', 'list'],
    queryFn: fetchPageSEOList,
    staleTime: 1000 * 60 * 10,
  });
}

// Page SEO mutations
export function usePageSEOMutations(): {
  syncPages: UseMutationResult<number, Error, void, unknown>;
  saveAll: UseMutationResult<
    number,
    Error,
    {
      selectedPage: Tables<'page_seo'>;
      languageData: unknown;
      t: (key: string, ...args: unknown[]) => string;
    },
    unknown
  >;
  generateSEO: UseMutationResult<
    unknown,
    Error,
    {
      selectedPage: Tables<'page_seo'>;
      selectedLocale: string;
      t: (key: string, ...args: unknown[]) => string;
    },
    unknown
  >;
} {
  const queryClient = useQueryClient();
  // Helper function to create page entries from entities
  const createPageEntries = (
    entities: Array<{ id: string; name?: string; title?: string; unique_code: string | null }>,
    type: string,
    urlPrefix: string
  ): Array<{ page_type: string; page_key: string; page_url: string; title: string }> => {
    return entities
      .map(entity => {
        const identifier = entity.unique_code ?? entity.id;
        if (!identifier) return null;
        return {
          page_type: type,
          page_key: `${type}-${identifier}`,
          page_url: `/${urlPrefix}/${identifier}`,
          title: entity.name ?? entity.title ?? identifier,
        };
      })
      .filter(
        (item): item is { page_type: string; page_key: string; page_url: string; title: string } =>
          item !== null
      );
  };

  // Helper function to fetch entity data
  const fetchEntityData = async () => {
    const [providers, doctors, treatments, blogPosts, procedures] = await Promise.all([
      supabase.from('providers').select('id, name, unique_code'),
      supabase.from('doctors').select('id, name, unique_code'),
      supabase.from('treatments').select('id, name, unique_code'),
      supabase.from('blog_posts').select('id, title, unique_code'),
      supabase.from('medical_procedures').select('id, name, unique_code'),
    ]);

    return { providers, doctors, treatments, blogPosts, procedures };
  };

  // Helper function to create locale records
  const createLocaleRecords = (
    pagesToCreate: Array<{ page_type: string; page_key: string; page_url: string; title: string }>,
    AVAILABLE_LOCALES: Array<{ code: string; name?: string; flag?: string }>
  ) => {
    const recordsToInsert: TablesInsert<'page_seo'>[] = [];
    for (const page of pagesToCreate) {
      for (const locale of AVAILABLE_LOCALES) {
        const localePageUrl =
          locale.code === 'en' ? page.page_url : `/${locale.code}${page.page_url}`;
        recordsToInsert.push({
          page_type: page.page_type,
          page_key: page.page_key,
          page_url: localePageUrl,
          language_code: locale.code,
          title: page.title,
          is_active: true,
        });
      }
    }
    return recordsToInsert;
  };

  const syncPages = useMutation({
    mutationFn: async () => {
      const { AVAILABLE_LOCALES, STATIC_PAGES } =
        await import('@/components/admin/admin-page-seo/constants');

      const { providers, doctors, treatments, blogPosts, procedures } = await fetchEntityData();

      const pagesToCreate = [
        ...createPageEntries(providers.data ?? [], 'provider', 'provider'),
        ...createPageEntries(doctors.data ?? [], 'doctor', 'doctor'),
        ...createPageEntries(treatments.data ?? [], 'treatment', 'treatment'),
        ...createPageEntries(blogPosts.data ?? [], 'blog', 'blog'),
        ...createPageEntries(procedures.data ?? [], 'medical_procedure', 'medical-procedure'),
        ...STATIC_PAGES,
      ];

      const recordsToInsert = createLocaleRecords(pagesToCreate, AVAILABLE_LOCALES);

      const { error } = await supabase.from('page_seo').upsert(recordsToInsert, {
        onConflict: 'page_key,language_code',
        ignoreDuplicates: false,
      });

      if (error) throw error;
      return recordsToInsert.length;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pageSeo'] });
    },
  });

  const createStructuredData = (
    formData: Record<string, unknown>,
    locale: string,
    pageUrl?: string
  ): Record<string, unknown> => {
    let structuredData: Record<string, unknown> = {};

    if (formData.schema_json_ld) {
      if (typeof formData.schema_json_ld === 'object' && !Array.isArray(formData.schema_json_ld)) {
        structuredData = formData.schema_json_ld as Record<string, unknown>;
      } else if (typeof formData.schema_json_ld === 'string' && formData.schema_json_ld.trim()) {
        try {
          structuredData = JSON.parse(formData.schema_json_ld) as Record<string, unknown>;
        } catch {
          structuredData = {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: formData.title,
            description: formData.meta_description,
            inLanguage: locale,
          };
        }
      }
    } else {
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: formData.title,
        description: formData.meta_description,
        inLanguage: locale,
      };
    }

    if (pageUrl) {
      structuredData.url = `https://medipaths.co${pageUrl}`;
      structuredData.mainEntityOfPage = {
        '@type': 'WebPage',
        '@id': `https://medipaths.co${pageUrl}`,
      };
    }

    if (formData.og_image_url) {
      structuredData.image = {
        '@type': 'ImageObject',
        url: formData.og_image_url,
        width: 1200,
        height: 630,
      };
    }

    return structuredData;
  };

  const generatePageUrl = async (
    selectedPage: Tables<'page_seo'>,
    locale: string,
    generateActualUrl: (pageKey: string, pageType: string, locale: string) => Promise<string>
  ): Promise<string> => {
    try {
      return await generateActualUrl(selectedPage.page_key, selectedPage.page_type, locale);
    } catch {
      const baseUrl = selectedPage.page_url ?? '';
      return locale === 'en' ? baseUrl : `/${locale}${baseUrl}`;
    }
  };

  const saveAll = useMutation({
    mutationFn: async ({
      selectedPage,
      languageData,
      t,
    }: {
      selectedPage: Tables<'page_seo'>;
      languageData: unknown;
      t: (key: string, ...args: unknown[]) => string;
    }) => {
      const { generateActualUrl } = await import('@/components/admin/admin-page-seo/utils');
      const { toast } = await import('sonner');

      const recordsToSave = [];

      for (const [locale, formData] of Object.entries(
        languageData as Record<string, Record<string, unknown>>
      )) {
        if (!formData.title || !formData.meta_description) {
          continue;
        }

        const pageUrl = await generatePageUrl(selectedPage, locale, generateActualUrl);
        const structuredData = createStructuredData(formData, locale, pageUrl ?? undefined);

        recordsToSave.push({
          page_type: selectedPage.page_type,
          page_key: selectedPage.page_key,
          language_code: locale,
          page_url: pageUrl,
          title: formData.title,
          meta_description: formData.meta_description,
          og_title: formData.og_title ?? formData.title,
          og_description: formData.og_description ?? formData.meta_description,
          og_image_url: formData.og_image_url,
          og_type: formData.og_type ?? 'website',
          canonical_url:
            formData.canonical_url ?? (pageUrl ? `https://medipaths.co${pageUrl}` : ''),
          robots_meta: formData.robots_meta ?? 'index, follow',
          structured_data: structuredData as Json,
          is_active: true,
        });
      }

      if (recordsToSave.length === 0) {
        toast.error(
          t(
            'seo_page_management',
            'no_complete_locales',
            'No complete language data to save. Please fill in at least Title and Meta Description for each language.'
          )
        );
        throw new Error('No complete locales');
      }

      const { error } = await supabase
        .from('page_seo')
        .upsert(recordsToSave as Tables<'page_seo'>[], {
          onConflict: 'page_key,language_code',
        });

      if (error) throw error;
      return recordsToSave.length;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pageSeo'] });
    },
  });

  const generateSEO = useMutation({
    mutationFn: async ({
      selectedPage,
      selectedLocale,
      t,
    }: {
      selectedPage: Tables<'page_seo'>;
      selectedLocale: string;
      t: (key: string, ...args: unknown[]) => string;
    }) => {
      const { toast } = await import('sonner');

      const { data, error } = (await supabase.functions.invoke('generate-page-seo', {
        body: JSON.stringify({
          pageType: selectedPage.page_type,
          pageKey: selectedPage.page_key,
          locale: selectedLocale,
        }),
      })) as { data: Record<string, unknown> | null; error: Error | null };

      if (error) {
        if (
          data &&
          typeof data === 'object' &&
          data !== null &&
          'error' in data &&
          typeof data.error === 'string'
        ) {
          throw new Error(data.error);
        }
        throw new Error(
          error &&
            typeof error === 'object' &&
            error !== null &&
            'message' in error &&
            typeof (error as unknown as Record<string, unknown>).message === 'string'
            ? ((error as unknown as Record<string, unknown>).message as string)
            : 'Edge Function returned an error. Please check if LOVABLE_API_KEY is configured in Supabase.'
        );
      }

      if (data && typeof data === 'object' && data !== null && 'seo' in data) {
        toast.success(
          t(
            'seo_page_management',
            'ai_content_loaded',
            'AI-generated SEO content loaded. Please review and save.'
          )
        );
        return data.seo;
      }

      return null;
    },
  });

  return { syncPages, saveAll, generateSEO };
}

// Bank accounts mutations
export function useBankAccountMutations(): {
  updateBankAccounts: UseMutationResult<
    { success: boolean },
    Error,
    { userId: string; bankAccounts: unknown[] },
    unknown
  >;
} {
  const queryClient = useQueryClient();

  const updateBankAccounts = useMutation({
    mutationFn: async ({ userId, bankAccounts }: { userId: string; bankAccounts: unknown[] }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ bank_accounts: bankAccounts as Json })
        .eq('id', userId);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.transportation.bankAccounts(variables.userId),
      });
    },
  });

  return { updateBankAccounts };
}

// Admin Doctors queries
export const fetchAdminDoctors = async (batchesToLoad = 1) => {
  const BATCH_SIZE = 100;
  const doctorsToLoad = batchesToLoad * BATCH_SIZE;
  const DOCTOR_COLUMNS = `
        id, display_order, created_at, updated_at, name, name_translations,
        unique_code, clinic_id, branch_id, provider_id, selected_clinics,
        linked_treatments, specialty, specialty_translations, description,
        description_translations, profile_image_url, profile_image_urls,
        profile_image_alt_text, languages_spoken, years_of_experience,
        education, before_after_photos, files, social_links
      `.replaceAll(/\s+/g, ' ');

  const [{ count }, { data }] = await Promise.all([
    supabase.from('doctors').select('id', { count: 'exact', head: true }),
    supabase
      .from('doctors')
      .select(DOCTOR_COLUMNS)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false })
      .range(0, doctorsToLoad - 1),
  ]);

  return {
    doctors: data as unknown as Database['public']['Tables']['doctors']['Row'][],
    totalCount: count ?? 0,
    hasMore: (count ?? 0) > doctorsToLoad,
  };
};

export function useAdminDoctors(batchesToLoad = 1): UseQueryResult<
  {
    doctors: Database['public']['Tables']['doctors']['Row'][];
    totalCount: number;
    hasMore: boolean;
  },
  Error
> {
  return useQuery({
    queryKey: queryKeys.admin.doctors([batchesToLoad]),
    queryFn: () => fetchAdminDoctors(batchesToLoad),
    staleTime: 1000 * 60 * 5,
  });
}

export const fetchAdminClinics = async () => {
  const { data, error } = await supabase
    .from('providers')
    .select('*')
    .order('name', { ascending: true });
  if (error) throw error;
  return data;
};

export function useAdminClinics(): UseQueryResult<
  Database['public']['Tables']['providers']['Row'][],
  Error
> {
  return useQuery({
    queryKey: queryKeys.admin.clinics,
    queryFn: () => fetchAdminClinics(),
    staleTime: 1000 * 60 * 15,
  });
}

export const fetchAdminProviders = async (batchesToLoad = 1) => {
  const BATCH_SIZE = 100;
  const providersToLoad = batchesToLoad * BATCH_SIZE;

  // Get total count
  const { count } = await supabase.from('providers').select('id', { count: 'exact', head: true });

  // Load providers using Provider.list for consistency with existing code
  const data = await Provider.list('-created_at');
  const providersList = Array.isArray(data) ? data : [];

  // Apply pagination
  const paginatedProviders = providersList.slice(0, providersToLoad);

  // Load provider admins for all providers
  const providerIds = paginatedProviders.map(provider => provider.id).filter(Boolean);
  let providerAdmins: { provider_id: string; admin_id: string }[] = [];

  if (providerIds.length > 0) {
    const { data: providerAdminsData, error: providerAdminsError } = await supabase
      .from('provider_admins')
      .select('provider_id, admin_id')
      .in('provider_id', providerIds);

    if (providerAdminsError) throw providerAdminsError;
    providerAdmins = providerAdminsData as { provider_id: string; admin_id: string }[];
  }

  const uniqueAdminIds = [...new Set(providerAdmins.map(entry => entry.admin_id).filter(Boolean))];

  let adminProfilesMap = new Map<string, { id: string; full_name: string }>();
  if (uniqueAdminIds.length > 0) {
    const { data: adminProfilesData, error: adminProfilesError } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', uniqueAdminIds);

    if (adminProfilesError) throw adminProfilesError;
    adminProfilesMap = new Map(
      adminProfilesData.map((profile: { id: string; full_name: string }) => [profile.id, profile])
    );
  }

  const adminsByProvider = providerAdmins.reduce((acc, { provider_id, admin_id }) => {
    if (!provider_id) return acc;
    if (!acc.has(provider_id)) {
      acc.set(provider_id, []);
    }
    if (admin_id) {
      const adminArray = acc.get(provider_id);
      if (adminArray) {
        adminArray.push(admin_id);
      }
    }
    return acc;
  }, new Map<string, string[]>());

  const enrichedData = paginatedProviders.map(provider => {
    const adminIds: string[] = adminsByProvider.get(provider.id) ?? [];
    const managingAdmins = adminIds
      .map((adminId: string) => adminProfilesMap.get(adminId))
      .filter(
        (
          admin: { id: string; full_name: string } | undefined
        ): admin is { id: string; full_name: string } => admin !== undefined
      );

    return {
      ...provider,
      managing_admin_ids: adminIds,
      managing_admins: managingAdmins,
      managing_admin_id: adminIds.length > 0 ? adminIds[0]! : null,
      managing_admin_name:
        managingAdmins.length > 0 ? (managingAdmins[0]?.full_name ?? null) : null,
    };
  });

  return {
    providers: enrichedData,
    totalCount: count ?? providersList.length,
    hasMore: (count ?? providersList.length) > providersToLoad,
  };
};

export function useAdminProviders(batchesToLoad = 1): UseQueryResult<{
  providers: (Tables<'providers'> & {
    managing_admin_ids: string[];
    managing_admins: { id: string; full_name: string }[];
    managing_admin_id: string | null;
    managing_admin_name: string | null;
  })[];
  totalCount: number;
  hasMore: boolean;
}> {
  return useQuery({
    queryKey: queryKeys.admin.providers([batchesToLoad]),
    queryFn: () => fetchAdminProviders(batchesToLoad),
    staleTime: 1000 * 60 * 5,
  });
}

export const fetchAdminProcedures = async (batchesToLoad = 1) => {
  const BATCH_SIZE = 100;
  const proceduresToLoad = batchesToLoad * BATCH_SIZE;
  const procedureColumns = `
        id,
        name,
        korean_name,
        slug,
        category,
        subcategory,
        general_description,
        common_alternative_names,
        generic_image_url,
        typical_duration_range,
        typical_recovery_range,
        market_price_range,
        general_benefits,
        general_risks,
        ideal_candidates_criteria,
        anesthesia_types,
        hospitalization_typically_required,
        result_longevity,
        common_faqs,
        procedure_steps_overview,
        search_keywords,
        related_procedures,
        featured,
        is_active,
        average_provider_rating,
        total_treatments_count,
        created_at,
        updated_at
      `.replaceAll(/\s+/g, ' ');
  const treatmentColumns = `
        id,
        name,
        korean_name,
        description,
        unique_code,
        category,
        medical_procedure_id,
        dedicated_provider_id,
        clinic_id
      `.replaceAll(/\s+/g, ' ');

  const [{ count }, proceduresResult, treatmentsResult, providersResult] = await Promise.all([
    supabase.from('medical_procedures').select('id', { count: 'exact', head: true }),
    supabase
      .from('medical_procedures')
      .select(procedureColumns)
      .order('created_at', { ascending: false })
      .range(0, proceduresToLoad - 1),
    supabase.from('treatments').select(treatmentColumns),
    supabase.from('providers').select('id, name'),
  ]);

  if (proceduresResult.error) throw proceduresResult.error;
  if (treatmentsResult.error) throw treatmentsResult.error;
  if (providersResult.error) throw providersResult.error;

  const procedureData = proceduresResult.data as unknown as Tables<'medical_procedures'>[];
  const treatmentData = treatmentsResult.data as unknown as Tables<'treatments'>[];
  const providersData = providersResult.data as { id: string; name: string }[];

  // Create a provider lookup map for quick access
  const providerMap: Record<string, string> = {};
  for (const provider of providersData) {
    providerMap[provider.id] = provider.name;
  }

  // Enhance treatment data with provider names
  const enhancedTreatmentData = (
    treatmentData as unknown as Database['public']['Tables']['treatments']['Row'][]
  ).map(treatment => ({
    ...treatment,
    provider_name: treatment.dedicated_provider_id
      ? (providerMap[treatment.dedicated_provider_id] ?? 'N/A')
      : treatment.clinic_id
        ? (providerMap[treatment.clinic_id] ?? 'N/A')
        : 'N/A',
  }));

  // Calculate treatment counts for each procedure
  const treatmentCountsMap = enhancedTreatmentData.reduce((acc, treatment) => {
    const key = treatment.medical_procedure_id;
    if (!key) return acc;
    acc.set(key, (acc.get(key) ?? 0) + 1);
    return acc;
  }, new Map<string, number>());

  const proceduresWithCounts = procedureData.map((procedure: Tables<'medical_procedures'>) => ({
    ...procedure,
    total_treatments_count: treatmentCountsMap.get(procedure.id) ?? 0,
  }));

  return {
    procedures: proceduresWithCounts,
    treatments: enhancedTreatmentData,
    totalCount: count ?? 0,
    hasMore: (count ?? 0) > proceduresToLoad,
  };
};

export function useAdminProcedures(batchesToLoad: number = 1) {
  return useQuery({
    queryKey: queryKeys.admin.procedures([batchesToLoad]),
    queryFn: () => fetchAdminProcedures(batchesToLoad),
    staleTime: 1000 * 60 * 5,
  });
}

export const fetchAdminTreatments = async (batchesToLoad = 1) => {
  const BATCH_SIZE = 100;
  const treatmentsToLoad = batchesToLoad * BATCH_SIZE;

  try {
    // First, try a simple count query
    const { count, error: countError } = await supabase
      .from('treatments')
      .select('id', { count: 'exact', head: true });

    if (countError) {
      console.error('fetchAdminTreatments: Count query error:', countError);
      throw countError;
    }

    // Simplified treatments query - include all fields needed by components
    const { data: treatmentsData, error: treatmentsError } = await supabase
      .from('treatments')
      .select(
        `
        id,
        unique_code,
        name,
        korean_name,
        description,
        category,
        clinic_id,
        dedicated_provider_id,
        created_at,
        updated_at,
        image_urls,
        image_alt_texts,
        name_translations,
        description_translations,
        cost_estimate_min,
        cost_estimate_max
      `
      )
      .order('created_at', { ascending: false })
      .range(0, treatmentsToLoad - 1);

    if (treatmentsError) {
      console.error('fetchAdminTreatments: Treatments query error:', treatmentsError);
      throw treatmentsError;
    }

    // Simplified clinics query
    const { data: clinicsData, error: clinicsError } = await supabase
      .from('providers')
      .select('id, name, city, district')
      .order('name', { ascending: true });

    if (clinicsError) {
      console.error('fetchAdminTreatments: Clinics query error:', clinicsError);
      throw clinicsError;
    }

    return {
      treatments: treatmentsData as unknown as Database['public']['Tables']['treatments']['Row'][],
      clinics: clinicsData as unknown as Database['public']['Tables']['providers']['Row'][],
      totalCount: count ?? 0,
      hasMore: (count ?? 0) > treatmentsToLoad,
    };
  } catch (error) {
    console.error('fetchAdminTreatments: Overall error:', error);
    throw error;
  }
};

export function useAdminTreatments(batchesToLoad: number = 1) {
  return useQuery({
    queryKey: queryKeys.admin.treatments([batchesToLoad]),
    queryFn: () => fetchAdminTreatments(batchesToLoad),
    staleTime: 1000 * 60 * 5,
  });
}

// Helper function to check if treatment is linked to a provider
function isTreatmentLinked(treatment: Database['public']['Tables']['treatments']['Row']): boolean {
  return Boolean(treatment.dedicated_provider_id) || Boolean(treatment.clinic_id);
}

// Helper function to check if treatment has cost range
function hasCostRange(treatment: Database['public']['Tables']['treatments']['Row']): boolean {
  const min = treatment.cost_estimate_min;
  const max = treatment.cost_estimate_max;
  return min !== null || max !== null;
}

// Helper function to check if treatment has images with alt texts
function hasImagesWithAltTexts(treatment: Database['public']['Tables']['treatments']['Row']): {
  hasImages: boolean;
  missingDescriptions: boolean;
} {
  const imageUrls = Array.isArray(treatment.image_urls) ? treatment.image_urls : [];
  if (imageUrls.length === 0) {
    return { hasImages: false, missingDescriptions: false };
  }

  const altTexts = Array.isArray(treatment.image_alt_texts) ? treatment.image_alt_texts : [];
  const describedCount = imageUrls.reduce((count: number, _: string, index: number) => {
    const altText = altTexts.at(index);
    return typeof altText === 'string' && altText.trim() ? count + 1 : count;
  }, 0);

  return { hasImages: true, missingDescriptions: describedCount < imageUrls.length };
}

// Helper function to check if a translation exists for a language
function hasTranslationForLanguage(
  lang: string,
  treatment: Database['public']['Tables']['treatments']['Row'],
  nameTranslations: Record<string, string>,
  descriptionTranslations: Record<string, string>
): boolean {
  const hasNameTranslation = Boolean(
    lang === 'ko'
      ? treatment.korean_name && treatment.korean_name.trim() !== ''
      : Object.prototype.hasOwnProperty.call(nameTranslations, lang) &&
          nameTranslations[lang]?.trim() !== ''
  );

  const hasDescriptionTranslation = Boolean(
    Object.prototype.hasOwnProperty.call(descriptionTranslations, lang) &&
    descriptionTranslations[lang]?.trim()
  );

  return hasNameTranslation && hasDescriptionTranslation;
}

// Helper function to count translations for a treatment
function countTranslations(treatment: Database['public']['Tables']['treatments']['Row']): number {
  const languages = ['ar', 'es', 'id', 'ja', 'ko', 'mn', 'ru', 'th', 'vi', 'zh'];
  const nameTranslations = treatment.name_translations as Record<string, string>;
  const descriptionTranslations = treatment.description_translations as Record<string, string>;

  let translatedCount = 1; // Always count English as translated

  for (const lang of languages) {
    if (hasTranslationForLanguage(lang, treatment, nameTranslations, descriptionTranslations)) {
      translatedCount++;
    }
  }
  return translatedCount;
}

// Helper function to calculate treatment statistics
function calculateTreatmentStats(treatments: Database['public']['Tables']['treatments']['Row'][]) {
  let notLinkedCount = 0;
  let missingCostRangeCount = 0;
  let missingImageDescriptionCount = 0;
  let treatmentsWithImagesCount = 0;
  let fullyTranslatedCount = 0;
  let partiallyTranslatedCount = 0;
  let totalTranslatedLanguages = 0;

  for (const treatment of treatments) {
    // Check if linked
    if (!isTreatmentLinked(treatment)) {
      notLinkedCount += 1;
    }

    // Check cost range
    if (!hasCostRange(treatment)) {
      missingCostRangeCount += 1;
    }

    // Check image descriptions
    const { hasImages, missingDescriptions } = hasImagesWithAltTexts(treatment);
    if (hasImages) {
      treatmentsWithImagesCount += 1;
      if (missingDescriptions) {
        missingImageDescriptionCount += 1;
      }
    }

    // Check translations
    const translatedCount = countTranslations(treatment);
    totalTranslatedLanguages += translatedCount;

    if (translatedCount === 11) {
      fullyTranslatedCount++;
    } else if (translatedCount > 1) {
      partiallyTranslatedCount++;
    }
  }

  return {
    notLinkedCount,
    missingCostRangeCount,
    missingImageDescriptionCount,
    treatmentsWithImagesCount,
    fullyTranslatedCount,
    partiallyTranslatedCount,
    totalTranslatedLanguages,
  };
}

// Helper function to calculate percentages
function calculatePercentages(
  stats: ReturnType<typeof calculateTreatmentStats>,
  totalTreatments: number
) {
  const totalLanguages = totalTreatments * 11; // 11 languages per treatment

  return {
    totalTreatments,
    notLinkedCount: stats.notLinkedCount,
    missingCostRangeCount: stats.missingCostRangeCount,
    notLinkedPercentage:
      totalTreatments > 0 ? Math.round((stats.notLinkedCount / totalTreatments) * 100) : 0,
    missingCostPercentage:
      totalTreatments > 0 ? Math.round((stats.missingCostRangeCount / totalTreatments) * 100) : 0,
    treatmentsWithImagesCount: stats.treatmentsWithImagesCount,
    missingImageDescriptionCount: stats.missingImageDescriptionCount,
    missingImageDescriptionPercentage:
      stats.treatmentsWithImagesCount > 0
        ? Math.round((stats.missingImageDescriptionCount / stats.treatmentsWithImagesCount) * 100)
        : 0,
    fullyTranslatedCount: stats.fullyTranslatedCount,
    partiallyTranslatedCount: stats.partiallyTranslatedCount,
    totalLanguages,
    translatedLanguages: stats.totalTranslatedLanguages,
    overallPercentage:
      totalLanguages > 0 ? Math.round((stats.totalTranslatedLanguages / totalLanguages) * 100) : 0,
  };
}

export function useAdminTreatmentStats(): UseQueryResult<{
  totalTreatments: number;
  notLinkedCount: number;
  missingCostRangeCount: number;
  notLinkedPercentage: number;
  missingCostPercentage: number;
  treatmentsWithImagesCount: number;
  missingImageDescriptionCount: number;
  missingImageDescriptionPercentage: number;
  fullyTranslatedCount: number;
  partiallyTranslatedCount: number;
  totalLanguages: number;
  translatedLanguages: number;
  overallPercentage: number;
}> {
  return useQuery({
    queryKey: queryKeys.admin.treatmentStats,
    queryFn: async () => {
      // Fetch only minimal fields needed for stats calculation
      const statsColumns = `
        id,
        dedicated_provider_id,
        clinic_id,
        cost_estimate_min,
        cost_estimate_max,
        image_urls,
        image_alt_texts,
        name_translations,
        description_translations,
        korean_name
      `.replaceAll(/\s+/g, ' ');

      const { data, error } = await supabase.from('treatments').select(statsColumns);

      if (error) throw error;

      const treatments = data as unknown as Database['public']['Tables']['treatments']['Row'][];

      const stats = calculateTreatmentStats(treatments);
      return calculatePercentages(stats, treatments.length);
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

// Image SEO queries
export function useImageSEOData({
  activeTab,
  entityTypeFilter,
  searchQuery,
  currentPage,
  itemsPerPage,
}: {
  activeTab: string;
  entityTypeFilter: string;
  searchQuery: string;
  currentPage: number;
  itemsPerPage: number;
}) {
  return useQuery({
    queryKey: [
      'imageSeo',
      'filtered',
      { activeTab, entityTypeFilter, searchQuery, currentPage, itemsPerPage },
    ],
    queryFn: async () => {
      const { fetchImagesWithFilters } =
        await import('@/components/admin/admin-image-seo/apiUtils');
      return fetchImagesWithFilters({
        activeTab,
        entityTypeFilter,
        searchQuery,
        currentPage,
        itemsPerPage,
      });
    },
    enabled: !!activeTab && !!itemsPerPage,
    staleTime: 1000 * 60 * 5,
  });
}

// Analytics queries
export function usePartnerProviders(
  assignedProviderIds: string[]
): UseQueryResult<Pick<Tables<'providers'>, 'id' | 'name' | 'unique_code'>[], Error> {
  return useQuery({
    queryKey: ['analytics', 'providers', assignedProviderIds],
    queryFn: async () => {
      if (assignedProviderIds.length === 0) return [];

      const isUUID = assignedProviderIds[0]?.match(/^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i);

      let query = supabase.from('providers').select('id, name, unique_code');

      query = isUUID
        ? query.in('id', assignedProviderIds)
        : query.in('unique_code', assignedProviderIds);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: assignedProviderIds.length > 0,
    staleTime: 1000 * 60 * 5,
  });
}

export function useProviderTreatments(
  providerId: string
): UseQueryResult<Pick<Tables<'treatments'>, 'unique_code' | 'name'>[]> {
  return useQuery({
    queryKey: ['analytics', 'treatments', providerId],
    queryFn: async () => {
      const providerIdStr = providerId;

      const { data, error } = await supabase
        .from('treatments')
        .select('unique_code, name')
        .or(`clinic_id.eq.${providerIdStr},dedicated_provider_id.eq.${providerIdStr}`)
        .order('name');

      if (error) throw error;

      return data;
    },
    enabled: !!providerId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useProviderDoctors(
  providerId: string
): UseQueryResult<Pick<Tables<'doctors'>, 'unique_code' | 'name'>[]> {
  return useQuery({
    queryKey: ['analytics', 'doctors', providerId],
    queryFn: async () => {
      const providerIdStr = providerId;

      const { data, error } = await supabase
        .from('doctors')
        .select('unique_code, name')
        .or(`clinic_id.eq.${providerIdStr},provider_id.eq.${providerIdStr}`)
        .order('name');

      if (error) throw error;

      return data;
    },
    enabled: !!providerId,
    staleTime: 1000 * 60 * 5,
  });
}

// Helper function to fetch related providers with optional strict filtering
async function fetchRelatedProviders(
  provider: Database['public']['Tables']['providers']['Row'],
  selectFields: string,
  useStrictFilters: boolean
): Promise<Tables<'providers'>[]> {
  let query = supabase.from('providers').select(selectFields).neq('id', provider.id);

  if (useStrictFilters) {
    // Apply strict filters for specialty and location matching
    if (provider.specialties.length) {
      query = query.overlaps('specialties', provider.specialties);
    }
    if (provider.city) {
      query = query.eq('city', provider.city);
    }
    query = query.limit(20);
  } else {
    // Broader fallback search
    query = query.limit(10);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data as unknown as Tables<'providers'>[] | null) ?? [];
}

// Helper function to build page views query with filters
function buildPageViewsQuery(
  providerId: string,
  dateRange: string,
  pageType: string,
  pageId: string,
  customFromDate: string,
  customToDate: string
) {
  let query = supabase
    .from('page_views')
    .select('*', { count: 'exact', head: false })
    .eq('provider_id', providerId)
    .limit(100000); // Set a high limit to avoid default 1000 cap

  if (dateRange === 'custom' && customFromDate && customToDate) {
    query = query
      .gte('created_at', `${customFromDate}T00:00:00.000Z`)
      .lte('created_at', `${customToDate}T23:59:59.999Z`);
  } else {
    const daysAgo = Number.parseInt(dateRange);
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysAgo);
    query = query.gte('created_at', startDate.toISOString());
  }

  if (pageType !== 'all') {
    query = query.eq('page_type', pageType);
    if (pageId !== 'all') {
      query = query.eq('page_id', pageId);
    }
  }

  return query;
}

// Helper function to fetch name mappings for treatments and doctors
async function fetchNameMappings(providerId: string): Promise<{
  treatmentNames: Record<string, string>;
  doctorNames: Record<string, string>;
}> {
  const providerIdStr = String(providerId);

  const [treatmentsResult, doctorsResult] = await Promise.all([
    supabase
      .from('treatments')
      .select('unique_code, name')
      .or(`clinic_id.eq.${providerIdStr},dedicated_provider_id.eq.${providerIdStr}`),
    supabase
      .from('doctors')
      .select('unique_code, name')
      .or(`clinic_id.eq.${providerIdStr},provider_id.eq.${providerIdStr}`),
  ]);

  const treatments = treatmentsResult.data ?? [];
  const doctors = doctorsResult.data ?? [];

  const treatmentNames: Record<string, string> = {};
  for (const t of treatments) {
    if (t.unique_code) {
      treatmentNames[t.unique_code] = t.name;
    }
  }

  const doctorNames: Record<string, string> = {};
  for (const d of doctors) {
    if (d.unique_code) {
      doctorNames[d.unique_code] = d.name;
    }
  }

  return { treatmentNames, doctorNames };
}

// Helper function to process daily views
function processDailyViews(views: Tables<'page_views'>[]): Array<{ date: string; views: number }> {
  const dailyViewsMap: Record<string, number> = {};

  for (const view of views) {
    const formattedDate =
      formatDate(view.created_at) ??
      (view.created_at ? new Date(view.created_at).toISOString().split('T')[0] : '') ??
      '';

    if (formattedDate in dailyViewsMap) {
      dailyViewsMap[formattedDate]! += 1;
    } else {
      dailyViewsMap[formattedDate] = 1;
    }
  }

  return Object.entries(dailyViewsMap)
    .map(([date, count]) => ({ date, views: count }))
    .sort((a, b) => new Date(a.date ?? '').getTime() - new Date(b.date ?? '').getTime());
}

// Helper function to get display name for a page view
function getPageDisplayName(
  view: Tables<'page_views'>,
  treatmentNames: Record<string, string>,
  doctorNames: Record<string, string>
): string {
  if (view.page_type === 'treatment' && view.page_id) {
    return treatmentNames[view.page_id] ?? view.page_id;
  }
  if (view.page_type === 'doctor' && view.page_id) {
    return doctorNames[view.page_id] ?? view.page_id;
  }
  if (view.page_type === 'provider') {
    return 'Provider Page';
  }
  return 'Main Page';
}

// Helper function to process page analytics
function processPageAnalytics(
  views: Tables<'page_views'>[],
  treatmentNames: Record<string, string>,
  doctorNames: Record<string, string>
): {
  pageTypeBreakdown: Array<{ name: string; value: number }>;
  topPages: Array<{ name: string; views: number }>;
  uniquePagesCount: number;
} {
  const pageTypeCount: Record<string, number> = {};
  const pageMap: Record<
    string,
    { type: string; id: string | null; displayName: string; count: number }
  > = {};

  for (const view of views) {
    // Page type breakdown
    const pageType = view.page_type;

    if (pageType in pageTypeCount) {
      pageTypeCount[pageType]! += 1;
    } else {
      pageTypeCount[pageType] = 1;
    }

    // Top pages
    const key = `${view.page_type}-${view.page_id}`;
    if (!(key in pageMap)) {
      pageMap[key] = {
        type: view.page_type,
        id: view.page_id,
        displayName: getPageDisplayName(view, treatmentNames, doctorNames),
        count: 0,
      };
    } else {
      pageMap[key]!.count++;
    }
  }

  const pageTypeBreakdown = Object.entries(pageTypeCount).map(([type, count]) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    value: count,
  }));

  const topPages = (
    Object.values(pageMap) as Array<{
      type: string;
      id: string | null;
      displayName: string;
      count: number;
    }>
  )
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
    .map(page => ({
      name: `${page.type.charAt(0).toUpperCase() + page.type.slice(1)}: ${page.displayName}`,
      views: page.count,
    }));

  const uniquePagesCount = Object.keys(pageMap).length;

  return { pageTypeBreakdown, topPages, uniquePagesCount };
}

export function useProviderAnalytics(
  providerId: string,
  dateRange: string,
  pageType: string,
  pageId: string,
  customFromDate: string,
  customToDate: string
): UseQueryResult<
  {
    totalViews: number;
    uniquePagesCount: number;
    dailyViews: Array<{ date: string; views: number }>;
    pageTypeBreakdown: Array<{ name: string; value: number }>;
    topPages: Array<{ name: string; views: number }>;
    recentViews: Tables<'page_views'>[];
  } | null,
  Error
> {
  return useQuery({
    queryKey: [
      'analytics',
      'pageViews',
      providerId,
      dateRange,
      pageType,
      pageId,
      customFromDate,
      customToDate,
    ],
    queryFn: async () => {
      if (!providerId) return null;

      const query = buildPageViewsQuery(
        providerId,
        dateRange,
        pageType,
        pageId,
        customFromDate,
        customToDate
      );
      const { data: allViews, error: viewsError } = await query;
      if (viewsError) throw viewsError;

      const views = allViews;
      const { treatmentNames, doctorNames } = await fetchNameMappings(providerId);

      const dailyViews = processDailyViews(views);
      const { pageTypeBreakdown, topPages, uniquePagesCount } = processPageAnalytics(
        views,
        treatmentNames,
        doctorNames
      );

      return {
        totalViews: views.length,
        uniquePagesCount,
        dailyViews,
        pageTypeBreakdown,
        topPages,
        recentViews: views.slice(0, 10),
      };
    },
    enabled: !!providerId,
    staleTime: 1000 * 60 * 2,
  });
}

// Reports queries
export const fetchReports = async (filter = 'pending'): Promise<Tables<'reports'>[]> => {
  const REPORT_SELECT_COLUMNS = `
        id,
        entity_id,
        entity_name,
        entity_type,
        report_reason,
        additional_details,
        reporter_email,
        reporter_user_id,
        status,
        created_at,
        updated_at
      `.replaceAll(/\s+/g, ' ');

  let query = supabase
    .from('reports')
    .select(REPORT_SELECT_COLUMNS)
    .order('created_at', { ascending: false });

  if (filter !== 'all') {
    query = query.eq('status', filter);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as unknown as Tables<'reports'>[];
};

export function useReports(filter = 'pending'): UseQueryResult<Tables<'reports'>[]> {
  return useQuery({
    queryKey: ['reports', filter],
    queryFn: () => fetchReports(filter),
    staleTime: 1000 * 60 * 2,
  });
}

// Reports mutations
export function useReportMutations(): {
  updateStatus: UseMutationResult<
    { reportId: string; newStatus: string },
    Error,
    { reportId: string; newStatus: string }
  >;
  remove: UseMutationResult<string, Error, string>;
} {
  const queryClient = useQueryClient();
  const updateStatus = useMutation({
    mutationFn: async ({
      reportId,
      newStatus,
    }: {
      reportId: string;
      newStatus: string;
    }): Promise<{ reportId: string; newStatus: string }> => {
      const { error } = await supabase
        .from('reports')
        .update({ status: newStatus })
        .eq('id', reportId);
      if (error) throw error;
      return { reportId, newStatus };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
  const remove = useMutation({
    mutationFn: async (reportId: string): Promise<string> => {
      const { error } = await supabase.from('reports').delete().eq('id', reportId);
      if (error) throw error;
      return reportId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
  return { updateStatus, remove };
}

// Stats queries
export function useStatsProviders(): UseQueryResult<number> {
  return useQuery({
    queryKey: queryKeys.stats.providers,
    queryFn: async () => {
      const { count } = await supabase
        .from('providers')
        .select('id', { count: 'exact', head: true });
      return count ?? 50;
    },
    staleTime: 1000 * 60 * 15,
  });
}

export function useStatsDoctors(): UseQueryResult<number> {
  return useQuery({
    queryKey: queryKeys.stats.doctors,
    queryFn: async () => {
      const { count } = await supabase.from('doctors').select('id', { count: 'exact', head: true });
      return count ?? 200;
    },
    staleTime: 1000 * 60 * 15,
  });
}

export function useStatsTreatments(): UseQueryResult<number> {
  return useQuery({
    queryKey: queryKeys.stats.treatments,
    queryFn: async () => {
      const { count } = await supabase
        .from('treatments')
        .select('id', { count: 'exact', head: true });
      return count ?? 500;
    },
    staleTime: 1000 * 60 * 15,
  });
}

export function useStatsEquipments(): UseQueryResult<number> {
  return useQuery({
    queryKey: queryKeys.stats.equipments,
    queryFn: async () => {
      const { count } = await supabase
        .from('equipment')
        .select('id', { count: 'exact', head: true });
      return count ?? 0;
    },
    staleTime: 1000 * 60 * 15,
  });
}

// Partners query
export function usePartners(): UseQueryResult<(Tables<'profiles'> & { is_partner: boolean })[]> {
  return useQuery({
    queryKey: queryKeys.partners.all,
    queryFn: async () => {
      const [{ data: profiles, error: profilesError }, { data: userRoles, error: rolesError }] =
        await Promise.all([
          supabase.from('profiles').select('*'),
          supabase.from('user_roles').select('*'),
        ]);
      if (profilesError) throw profilesError;
      if (rolesError) throw rolesError;
      const allPartners = profiles
        .map(profile => {
          const roles = new Set(userRoles.filter(r => r.user_id === profile.id).map(r => r.role));
          return {
            ...profile,
            is_partner: roles.has('partner') || roles.has('admin'),
          };
        })
        .filter(u => u.is_partner);
      return allPartners;
    },
    staleTime: 1000 * 60 * 10,
  });
}

// Favorites queries
export function useFavorites(userId: string): UseQueryResult<Tables<'user_favorites'>[]> {
  return useQuery({
    queryKey: queryKeys.favorites.all(userId),
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from('user_favorites')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
}

// Glossary queries
export function useGlossary(): UseQueryResult<Tables<'glossary'>[]> {
  return useQuery({
    queryKey: ['glossary', 'all'],
    queryFn: async () => {
      const pageSize = 1000;
      let allTerms: Tables<'glossary'>[] = [];
      let from = 0;
      let to = pageSize - 1;

      for (;;) {
        const { data, error, count } = await supabase
          .from('glossary')
          .select(
            'id, term, description, letter, archived, archived_at, created_at, updated_at, sources:glossary_sources(id, source_type, source_table, source_name, source_id, source_field, context, source_key)',
            { count: from === 0 ? 'exact' : 'planned' }
          )
          .order('term')
          .range(from, to);

        if (error) throw error;
        if (data.length === 0) break;

        allTerms = [...allTerms, ...data];

        if (from === 0 && count !== null && count <= pageSize) break;
        if (data.length < pageSize) break;

        from += pageSize;
        to += pageSize;
      }

      return allTerms;
    },
    staleTime: 1000 * 60 * 10,
  });
}

// Glossary mutations
export function useGlossaryMutations() {
  const queryClient = useQueryClient();

  const create = useMutation({
    mutationFn: async (termData: TablesInsert<'glossary'>) => {
      const { data, error } = await supabase.from('glossary').insert([termData]).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['glossary'] });
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: TablesUpdate<'glossary'> }) => {
      const { data, error } = await supabase
        .from('glossary')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['glossary'] });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('glossary').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['glossary'] });
    },
  });

  return { create, update, remove };
}

// Chat logs queries
export function useChatLogs(
  page = 1,
  pageSize = 10
): UseQueryResult<{
  data: (Tables<'chat_history'> & { providerName: string; messageCount: number })[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}> {
  const offset = (page - 1) * pageSize;

  return useQuery({
    queryKey: ['chatLogs', 'paginated', page, pageSize],
    queryFn: async () => {
      // Get total count first
      const { count: totalCount, error: countError } = await supabase
        .from('chat_history')
        .select('*', { count: 'exact', head: true });

      if (countError) throw countError;
      const safeTotalCount = totalCount ?? 0;

      // Fetch paginated chat history
      const { data: chatData, error: chatError } = await supabase
        .from('chat_history')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + pageSize - 1);

      if (chatError) throw chatError;

      // Get unique provider IDs
      const providerIds = [...new Set(chatData.map(chat => chat.provider_id))];

      // Fetch provider names
      const { data: providersData } = await supabase
        .from('providers')
        .select('id, name')
        .in('id', providerIds);

      const providersMap: Record<string, string> = {};
      if (providersData)
        for (const p of providersData) {
          providersMap[p.id] = p.name;
        }

      // Enrich chat data with provider names
      const enrichedChats = chatData.map(chat => ({
        ...chat,
        providerName: providersMap[chat.provider_id] ?? 'Unknown Provider',
        messageCount: Array.isArray(chat.messages) ? chat.messages.length : 0,
      }));

      return {
        data: enrichedChats,
        totalCount: safeTotalCount,
        page,
        pageSize,
        totalPages: Math.ceil(safeTotalCount / pageSize),
      };
    },
    staleTime: 1000 * 60 * 5,
  });
}

// Partners data hook with profiles and roles
export function usePartnersData(): UseQueryResult<{
  partners: (Tables<'profiles'> & {
    is_partner: boolean;
    is_admin: boolean;
    roles: string[];
  })[];
  providers: Tables<'providers'>[];
  allUsers: (Tables<'profiles'> & {
    is_partner: boolean;
    is_admin: boolean;
    roles: string[];
  })[];
  availableUsers: (Tables<'profiles'> & {
    is_partner: boolean;
    is_admin: boolean;
    roles: string[];
  })[];
}> {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['partners', 'data'],
    queryFn: async () => {
      // Get all users from profiles table
      const { data: profiles, error: profilesError } = await supabase.from('profiles').select('*');

      if (profilesError) throw profilesError;

      // Get user roles
      const { data: userRoles, error: rolesError } = await supabase.from('user_roles').select('*');

      if (rolesError) throw rolesError;

      // Get providers

      const allProviders = await queryClient.ensureQueryData({
        queryKey: queryKeys.providers.all,
        queryFn: () => Provider.list(),
      });

      // Merge profiles with roles

      const users = profiles.map(profile => {
        const roles = userRoles.filter(r => r.user_id === profile.id).map(r => r.role);
        return {
          ...profile,
          is_partner: roles.includes('partner'),
          is_admin: roles.includes('admin'),
          roles,
        };
      });

      const partners = users.filter(u => u.is_partner || u.is_admin);
      const availableUsers = users.filter(u => !u.is_partner && !u.is_admin);

      return {
        partners,
        providers: allProviders,
        allUsers: users,
        availableUsers,
      };
    },
    staleTime: 1000 * 60 * 5,
  });
}

// Transportation requests
export function useTransportationRequests(
  partnerId: string
): UseQueryResult<Tables<'transportation_requests'>[]> {
  return useQuery({
    queryKey: queryKeys.transportation.requests(partnerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transportation_requests')
        .select(
          `
          *,
          inquiries (
            patient_name,
            patient_email,
            phone,
            country,
            clinic_id
          )
        `
        )
        .eq('third_party_partner_id', partnerId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data;
    },
    enabled: !!partnerId,
    staleTime: 1000 * 60 * 5,
  });
}

// Transportation partners
export function useTransportationPartners(): UseQueryResult<Tables<'profiles'>[]> {
  return useQuery({
    queryKey: queryKeys.transportation.partners,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('partner_category', '3rd_party')
        .contains('third_party_services', ['transportation']);

      if (error) throw error;

      return data;
    },
    staleTime: 1000 * 60 * 15,
  });
}

// Bank accounts
interface BankAccount {
  id: string;
  bankName: string;
  accountHolder: string;
  accountNumber: string;
}

export function useBankAccounts(userId: string): UseQueryResult<BankAccount[]> {
  return useQuery({
    queryKey: queryKeys.transportation.bankAccounts(userId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('bank_accounts')
        .eq('id', userId)
        .single();

      if (error) throw error;
      const bankAccounts = data.bank_accounts;
      return Array.isArray(bankAccounts) ? (bankAccounts as unknown as BankAccount[]) : [];
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
}

// Partner doctors (filtered by assigned provider IDs)
export function usePartnerDoctors(
  assignedProviderIds: string[]
): UseQueryResult<Tables<'doctors'>[]> {
  return useQuery({
    queryKey: queryKeys.partners.doctors(assignedProviderIds),
    queryFn: async () => {
      if (assignedProviderIds.length === 0) return [];

      const { data, error } = await supabase
        .from('doctors')
        .select('*')
        .or(assignedProviderIds.map(id => `clinic_id.eq.${id}`).join(','));

      if (error) throw error;

      return data;
    },
    enabled: !!assignedProviderIds && assignedProviderIds.length > 0,
    staleTime: 1000 * 60 * 5,
  });
}

// Partner clinics (filtered by assigned provider IDs)
export function usePartnerClinics(
  assignedProviderIds: string[]
): UseQueryResult<Tables<'providers'>[]> {
  return useQuery({
    queryKey: queryKeys.partners.clinics(assignedProviderIds),
    queryFn: async () => {
      if (assignedProviderIds.length === 0) return [];

      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .in('id', assignedProviderIds);

      if (error) throw error;

      return data;
    },
    enabled: !!assignedProviderIds && assignedProviderIds.length > 0,
    staleTime: 1000 * 60 * 5,
  });
}

// Helper function to get preview data from storage
function _getPreviewData(previewId: string): Tables<'treatments'> | null {
  let previewData = sessionStorage.getItem(previewId);
  previewData ??= localStorage.getItem(previewId);
  return previewData ? (JSON.parse(previewData) as Tables<'treatments'>) : null;
}

// Helper function to find treatment by exact ID
async function _findTreatmentByExactId(treatmentId: string): Promise<Tables<'treatments'> | null> {
  if (treatmentId.length !== 36 && treatmentId.length !== 32) return null;

  const { data, error } = await supabase
    .from('treatments')
    .select('*')
    .eq('id', treatmentId)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') return null;
  return data ?? null;
}

// Helper function to find treatment by exact code
async function _findTreatmentByExactCode(
  code: string | null
): Promise<Tables<'treatments'> | null> {
  if (!code) return null;

  const { data, error } = await supabase
    .from('treatments')
    .select('*')
    .eq('unique_code', code)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') return null;
  return data ?? null;
}

// Helper function to find treatment by code prefix
async function _findTreatmentByCodePrefix(
  code: string | null
): Promise<Tables<'treatments'> | null> {
  if (!code) return null;

  const { data, error } = await supabase
    .from('treatments')
    .select('*')
    .ilike('unique_code', `${code}%`)
    .order('updated_at', { ascending: false })
    .limit(1);

  if (error && error.code !== 'PGRST116') return null;
  return data && data.length > 0 ? (data[0] as Tables<'treatments'>) : null;
}

// Helper function to find treatment by name
async function _findTreatmentByName(name: string | null): Promise<Tables<'treatments'> | null> {
  if (!name) return null;

  const { data, error } = await supabase
    .from('treatments')
    .select('*')
    .ilike('name', `%${name}%`)
    .order('updated_at', { ascending: false })
    .limit(1);

  if (error && error.code !== 'PGRST116') return null;
  return data && data.length > 0 ? (data[0] as Tables<'treatments'>) : null;
}

// Helper function to find treatment by ID prefix
async function _findTreatmentByIdPrefix(shortId: string): Promise<Tables<'treatments'> | null> {
  if (shortId.length !== 8) return null;

  try {
    const { data: recentTreatments, error: recentError } = await supabase
      .from('treatments')
      .select('id')
      .order('updated_at', { ascending: false })
      .limit(2000);

    if (recentError || !recentTreatments) return null;

    const matched = recentTreatments.find(t =>
      t.id?.toLowerCase().startsWith(shortId.toLowerCase())
    );

    if (!matched) return null;

    const { data: fullTreatment, error: fullError } = await supabase
      .from('treatments')
      .select('*')
      .eq('id', matched.id)
      .maybeSingle();

    return fullError ? null : fullTreatment;
  } catch {
    return null;
  }
}

// Treatment detail query
export function useTreatmentDetail(
  treatmentId: string,
  treatmentCode: string,
  treatmentName: string,
  previewId: string
): UseQueryResult<Tables<'treatments'>> {
  return useQuery({
    queryKey: ['treatment', 'detail', treatmentId, treatmentCode, treatmentName, previewId],
    queryFn: async () => {
      // Validate that we have at least one search parameter
      if (!treatmentId && !treatmentCode && !treatmentName && !previewId) {
        throw new Error('No search parameters provided for treatment lookup');
      }

      // Check if this is preview mode
      if (previewId) {
        const previewData = _getPreviewData(previewId);
        if (previewData) return previewData;
      }

      // Determine effective code for searching
      const effectiveCode = treatmentCode || (treatmentId?.length === 8 ? treatmentId : null);

      // Try different search strategies in order of preference
      const searchStrategies = [
        () => _findTreatmentByExactId(treatmentId),
        () => _findTreatmentByExactCode(effectiveCode),
        () => _findTreatmentByCodePrefix(effectiveCode),
        () => _findTreatmentByName(treatmentName),
        () => _findTreatmentByIdPrefix(treatmentId),
      ];

      for (const strategy of searchStrategies) {
        const result = await strategy();
        if (result) return result;
      }

      // If no treatment found, throw error
      throw new Error('Treatment not found');
    },
    enabled: !!(treatmentId || treatmentCode || treatmentName || previewId),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
}

// Helper function to build provider location query
const buildProviderLocationQuery = (selectColumns: string, city: string, district: string) => {
  const isFiltered = Boolean(city || district);

  let baseQuery = supabase
    .from('providers')
    .select(selectColumns)
    .order('created_at', { ascending: false });

  if (isFiltered) {
    if (city) {
      baseQuery = baseQuery.eq('city', city);
    }
    if (district) {
      baseQuery = baseQuery.eq('district', district);
    }
    baseQuery = baseQuery.or('is_branch_provider.is.null,is_branch_provider.eq.false');
  } else {
    baseQuery = baseQuery.limit(200);
  }

  return baseQuery;
};

// Providers by location (city/district)
export function useProvidersByLocation(
  city: string,
  district: string,
  options: Record<string, unknown> = {}
): UseQueryResult<(Tables<'providers'> & { phone: string | null })[]> {
  return useQuery({
    queryKey: ['providers', 'location', city || 'all', district || 'all'],
    queryFn: async () => {
      const PROVIDER_SELECT_COLUMNS = `
        id,
        name,
        image_urls,
        available_treatments,
        contact_phone,
        city,
        district,
        branches,
        description,
        specialties,
        specialty,
        location,
        is_branch_provider,
        created_at,
        updated_at
      `.replaceAll(/\s+/g, ' ');

      const baseQuery = buildProviderLocationQuery(PROVIDER_SELECT_COLUMNS, city, district);

      const { data: primaryData, error: primaryError } = await baseQuery;
      if (primaryError) throw primaryError;

      let combinedProviders =
        primaryData as unknown as Database['public']['Tables']['providers']['Row'][];

      const isFiltered = Boolean(city || district);
      if (isFiltered) {
        const { data: branchProviders, error: branchError } = await supabase
          .from('providers')
          .select(PROVIDER_SELECT_COLUMNS)
          .eq('is_branch_provider', true);

        if (branchError) throw branchError;

        const typedBranchProviders =
          branchProviders as unknown as Database['public']['Tables']['providers']['Row'][];

        if (typedBranchProviders.length > 0) {
          const baseIds = new Set(combinedProviders.map(provider => provider.id));

          const matchingBranchProviders = typedBranchProviders.filter(clinic => {
            const branches = clinic.branches as unknown[];
            if (!branches || branches.length === 0) {
              return false;
            }
            return branches.some((branch: unknown) => {
              const branchData = branch as Branch;
              const matchesCity = !city || branchData.city === city;
              const matchesDistrict = !district || branchData.district === district;
              return matchesCity && matchesDistrict;
            });
          });

          for (const provider of matchingBranchProviders) {
            if (!baseIds.has(provider.id)) {
              combinedProviders.push(provider);
            }
          }
        }
      }

      // Map contact_phone to phone for backward compatibility
      return combinedProviders.map((provider: Tables<'providers'>) => ({
        ...provider,
        phone: provider.contact_phone,
      }));
    },
    enabled: options.enabled !== false,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
}

// Provider chat mutations
export function useProviderChatMutation() {
  return useMutation({
    mutationFn: async ({
      providerId,
      messages,
      userLocale,
      sessionId,
      userId,
    }: {
      providerId: string;
      messages: unknown[];
      userLocale: string;
      sessionId: string;
      userId: string | null;
    }) => {
      const { data, error } = (await supabase.functions.invoke('provider-chat', {
        body: {
          providerId,
          messages,
          userLocale,
          sessionId,
          userId,
        },
      })) as { data: Record<string, unknown> | null; error: Error | null };

      if (error) {
        throw error;
      }
      if (data && typeof data === 'object' && 'error' in data && data.error) {
        throw new Error(String(data.error as string));
      }
      return data as Record<string, unknown>;
    },
  });
}

// Chat analytics mutation
export function useChatAnalyticsMutation() {
  const _queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      providerId,
      sessionId,
      userId,
      messageCount,
      ledToBooking,
    }: {
      providerId: string;
      sessionId: string;
      userId: string | null;
      messageCount: number;
      ledToBooking: boolean;
    }) => {
      const { error } = await supabase.from('chat_analytics').upsert(
        {
          provider_id: providerId,
          session_id: sessionId,
          user_id: userId ?? null,
          message_count: messageCount,
          led_to_booking: ledToBooking ?? false,
        },
        { onConflict: 'session_id', ignoreDuplicates: false }
      );

      if (error) throw error;
    },
    onSuccess: () => {
      // Could invalidate analytics queries if needed
    },
  });
}

// Admin mutations for provider management
export function useProviderAdminMutations() {
  const queryClient = useQueryClient();

  const updateAdminAssignment = useMutation({
    mutationFn: async ({
      providerId,
      adminId,
      shouldAdd,
    }: {
      providerId: string;
      adminId: string;
      shouldAdd: boolean;
    }) => {
      if (shouldAdd) {
        const { error } = await supabase
          .from('provider_admins')
          .insert({ provider_id: providerId, admin_id: adminId });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('provider_admins')
          .delete()
          .eq('provider_id', providerId)
          .eq('admin_id', adminId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'providers'] });
      void queryClient.invalidateQueries({ queryKey: ['providers'] });
    },
  });

  const updateAdminMemo = useMutation({
    mutationFn: async ({ clinicId, memo }: { clinicId: string; memo: string }) => {
      const { error } = await supabase
        .from('providers')
        .update({ admin_memo: memo })
        .eq('id', clinicId);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'providers'] });
      void queryClient.invalidateQueries({ queryKey: ['providers'] });
    },
  });

  const updateAdminTodos = useMutation({
    mutationFn: async ({ clinicId, todos }: { clinicId: string; todos: unknown[] }) => {
      const { error } = await supabase
        .from('providers')
        .update({ admin_todos: todos as Json })
        .eq('id', clinicId);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'providers'] });
      void queryClient.invalidateQueries({ queryKey: ['providers'] });
    },
  });

  return {
    updateAdminAssignment,
    updateAdminMemo,
    updateAdminTodos,
  };
}

// Chat log query
export function useChatLog(sessionId: string): UseQueryResult<
  | (Tables<'chat_history'> & {
      providerName: string | null;
      analytics: Tables<'chat_analytics'> | null;
    })
  | null
> {
  return useQuery({
    queryKey: ['chatLog', sessionId],
    queryFn: async () => {
      // Fetch chat history
      const { data: historyData, error: historyError } = await supabase
        .from('chat_history')
        .select('*')
        .eq('session_id', sessionId)
        .maybeSingle();

      if (historyError) throw historyError;
      if (!historyData) return null;

      // Fetch provider name
      const { data: providerData } = await supabase
        .from('providers')
        .select('name')
        .eq('id', historyData.provider_id)
        .single();

      // Fetch analytics
      const { data: analyticsData } = await supabase
        .from('chat_analytics')
        .select('*')
        .eq('session_id', sessionId)
        .single();

      return {
        ...historyData,
        providerName: providerData?.name ?? null,
        analytics: analyticsData,
      };
    },
    enabled: !!sessionId,
    staleTime: 1000 * 60 * 5,
  });
}
